import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ExportButton from '../../../components/ExportButton';
import BulkActions from '../../../components/BulkActions';
import AgentSidebar from './AgentSidebar';
import '../Admin.css';
import { TextField, Checkbox } from '@mui/material';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Table,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Progress,
  Alert,
  ButtonGroup,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  getAdminAgents,
  getAgentStats,
  startAgent,
  stopAgent,
  restartAgent,
  activateAgent,
  deactivateAgent,
  bulkAgentAction,
  bulkUpdateAgentStatus,
  getPerformanceMetrics,
  getCostAnalytics,
  getAgentLogs,
  getAgentCalls,
  getAgentPerformance,
  exportAdminAgents
} from '../../../store/Admin';
import ViewStateManager from '../../../utils/viewStateManager';

const AgentsManagement = () => {
  const dispatch = useDispatch();
  const { adminAgents, agentStats, performanceMetrics, costAnalytics, agentLogs, agentCalls, agentPerformance, agentsPagination, loading } = useSelector(state => state['Admin']);

  const [activeTab, setActiveTab] = useState(() => ViewStateManager.getAdminSubTab('agents', 'overview')['activeTab']);
  const [currentPage, setCurrentPage] = useState(1);

  // Persist sub-tab selection so a refresh restores the same tab
  useEffect(() => {
    ViewStateManager.setAdminSubTab('agents', { activeTab });
  }, [activeTab]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [selectedAgentForDetails, setSelectedAgentForDetails] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectAll, setSelectAll] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const saved = localStorage.getItem('adminAgentsAutoRefresh');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [refreshInterval, setRefreshInterval] = useState(30000);
  const [exportLoading, setExportLoading] = useState(false);

  // Save autoRefresh preference to localStorage
  useEffect(() => {
    localStorage.setItem('adminAgentsAutoRefresh', JSON.stringify(autoRefresh));
  }, [autoRefresh]);

  useEffect(() => {
    // Load data based on active tab
    dispatch(getAgentStats());
    dispatch(getAdminAgents({ 'page': currentPage, 'search': searchTerm, 'status': statusFilter }));
    dispatch(getPerformanceMetrics({ 'period': '7d' }));
    dispatch(getCostAnalytics({ 'period': '30d' }));

    // Load agent performance data for monitoring
    if (activeTab === 'performance') {
      dispatch(getAgentPerformance({ 'period': '7d' }));
    }
  }, [currentPage, searchTerm, statusFilter, dispatch]);

  // Separate useEffect for auto-refresh to prevent unwanted refreshes
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      if (activeTab === 'overview') {
        dispatch(getAgentStats());
        dispatch(getPerformanceMetrics({ 'period': '7d' }));
        dispatch(getCostAnalytics({ 'period': '30d' }));
      }
      if (activeTab === 'performance') {
        dispatch(getAgentPerformance({ 'period': '7d' }));
      }
      setLastUpdate(new Date());
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [activeTab, autoRefresh, refreshInterval, dispatch]);

  // Handle tab changes and load specific data
  useEffect(() => {
    if (activeTab === 'overview') {
      dispatch(getAgentStats());
      dispatch(getPerformanceMetrics({ 'period': '7d' }));
      dispatch(getCostAnalytics({ 'period': '30d' }));
    } else if (activeTab === 'performance') {
      dispatch(getAgentPerformance({ 'period': '7d' }));
    } else if (activeTab === 'agent-list') {
      dispatch(getAdminAgents({ 'page': currentPage, 'search': searchTerm, 'status': statusFilter }));
    }
  }, [activeTab, dispatch]);

  const handleAgentAction = (agentId, action) => {
    const filters = { 'page': currentPage, 'search': searchTerm, 'status': statusFilter };

    if (action === 'activate') {
      dispatch(activateAgent({ 'id': agentId, 'filters': filters }));
    }
    if (action === 'deactivate') {
      dispatch(deactivateAgent({ 'id': agentId, 'filters': filters }));
    }
    if (action === 'start') dispatch(startAgent({ 'id': agentId, 'filters': filters }));
    if (action === 'stop') dispatch(stopAgent({ 'id': agentId, 'filters': filters }));
    if (action === 'restart') dispatch(restartAgent({ 'id': agentId, 'filters': filters }));
  };

  const handleBulkAction = async (action, agentIds, data = {}) => {
    const filters = { 'page': currentPage, 'search': searchTerm, 'status': statusFilter };

    switch (action) {
      case 'activate':
        dispatch(bulkUpdateAgentStatus({ 'agentIds': agentIds, 'status': 'active', 'filters': filters }));
        break;
      case 'deactivate':
        dispatch(bulkUpdateAgentStatus({ 'agentIds': agentIds, 'status': 'inactive', 'filters': filters }));
        break;
      case 'start':
        dispatch(bulkAgentAction({ 'action': 'start', 'agentIds': agentIds, 'filters': filters }));
        break;
      case 'stop':
        dispatch(bulkAgentAction({ 'action': 'stop', 'agentIds': agentIds, 'filters': filters }));
        break;
      case 'restart':
        dispatch(bulkAgentAction({ 'action': 'restart', 'agentIds': agentIds, 'filters': filters }));
        break;
      case 'delete':
        dispatch(bulkAgentAction({ 'action': 'delete', 'agentIds': agentIds, 'filters': filters }));
        break;
      case 'assign-workspace':
        dispatch({ 'type': 'BULK_ASSIGN_WORKSPACE', 'payload': { 'agentIds': agentIds, 'workspaceId': data['workspaceId'], 'filters': filters } });
        break;
      default:
        console.warn('Unknown bulk action:', action);
    }

    // Clear selection after action
    setSelectedAgents([]);
    setSelectAll(false);
  };

  const loadAgentDetails = (agentId) => {
    // Dispatch actions to load agent details, logs, and calls
    dispatch(getAgentLogs({ 'id': agentId, 'page': 1, 'limit': 50 }));
    dispatch(getAgentCalls({ 'id': agentId, 'page': 1, 'limit': 50 }));
  };

  const openAgentDetails = (agent) => {
    setSelectedAgentForDetails(agent);
    setShowSidebar(true);
  };

  const closeSidebar = () => {
    setShowSidebar(false);
    setTimeout(() => setSelectedAgentForDetails(null), 300);
  };

  const getStatusBadge = (status, agent) => {
    const statusConfig = {
      'active': { color: 'success', label: 'Active', icon: '✓' },
      'inactive': { color: 'secondary', label: 'Inactive', icon: '✗' },
      'busy': { color: 'warning', label: 'Busy', icon: '⏳' },
      'error': { color: 'danger', label: 'Error', icon: '⚠' }
    };
    
    // Check if agent is running (if running field exists)
    if (agent && agent.running !== undefined) {
      if (status === 'active' && agent.running) {
        return (
          <span>
            <Badge color="success">
              <span style={{ marginRight: '4px' }}>🟢</span>
              Running
            </Badge>
          </span>
        );
      } else if (status === 'active' && !agent.running) {
        return (
          <span>
            <Badge color="warning">
              <span style={{ marginRight: '4px' }}>🟡</span>
              Stopped
            </Badge>
          </span>
        );
      }
    }
    
    const config = statusConfig[status] || statusConfig['active'];
    return (
      <Badge color={config.color}>
        <span style={{ marginRight: '4px' }}>{config.icon}</span>
        {config.label}
      </Badge>
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAgents([]);
    } else {
      setSelectedAgents(adminAgents.map(agent => agent['_id']));
    }
    setSelectAll(!selectAll);
  };

  const handleExportAllAgents = (format) => {
    setExportLoading(true);

    dispatch(exportAdminAgents({
      search: searchTerm,
      status: statusFilter,
      callback: (exportData, error) => {
        setExportLoading(false);

        if (error) {
          console.error('Export failed:', error);
          return;
        }

        if (!exportData?.agents) {
          console.error('No export data received');
          return;
        }

        // Use the exportFormattedData function with all agents
        import('../../../utils/exportData').then(({ exportFormattedData }) => {
          const exportFilename = `all-agents-${new Date().toISOString().split('T')[0]}`;

          exportFormattedData(
            exportData.agents,
            [
              { 'key': 'agentName', 'name': 'Agent Name' },
              { 'key': 'description', 'name': 'Description' },
              { 'key': 'workspaceName', 'name': 'Workspace' },
              { 'key': 'statusText', 'name': 'Status' },
              { 'key': 'totalCalls', 'name': 'Total Calls' },
              { 'key': 'successfulCalls', 'name': 'Successful Calls' },
              { 'key': 'successRate', 'name': 'Success Rate' },
              { 'key': 'averageCallDuration', 'name': 'Avg Duration' },
              { 'key': 'createdDate', 'name': 'Created' },
              { 'key': 'lastActivityDate', 'name': 'Last Activity' }
            ],
            format,
            `${exportFilename}.${format === 'excel' ? 'xlsx' : format}`
          );
        });
      }
    }));
  };

  const handleDeselectAll = () => {
    setSelectedAgents([]);
    setSelectAll(false);
  };

  const handleAgentSelect = (agentId) => {
    setSelectedAgents(prev => {
      const newSelected = prev.includes(agentId) ? prev.filter(id => id !== agentId) : [...prev, agentId];
      setSelectAll(newSelected.length === adminAgents.length);
      return newSelected;
    });
  };

  const bulkActions = [
    { 'key': 'activate', 'type': 'activate', 'label': 'Activate', 'description': 'Activate selected agents', 'requiresConfirmation': true, 'confirmationMessage': 'This will activate all selected agents and allow them to process calls.' },
    { 'key': 'deactivate', 'type': 'deactivate', 'label': 'Deactivate', 'description': 'Deactivate selected agents', 'requiresConfirmation': true, 'confirmationMessage': 'This will deactivate all selected agents.', 'warning': 'Deactivated agents cannot process any calls until reactivated.' },
    { 'key': 'start', 'type': 'start', 'label': 'Start', 'description': 'Start selected agents', 'requiresConfirmation': true, 'confirmationMessage': 'This will start all selected active agents.' },
    { 'key': 'stop', 'type': 'stop', 'label': 'Stop', 'description': 'Stop selected agents', 'requiresConfirmation': true, 'confirmationMessage': 'This will stop all selected agents.', 'warning': 'Stopped agents will not handle new calls.' },
    { 'key': 'restart', 'type': 'restart', 'label': 'Restart', 'description': 'Restart selected agents', 'requiresConfirmation': true, 'confirmationMessage': 'This will restart all selected agents.' },
    // { 'key': 'assign-workspace', 'type': 'assign', 'label': 'Assign Workspace', 'description': 'Move selected agents to a different workspace', 'requiresConfirmation': true, 'fields': [{ 'key': 'workspaceId', 'label': 'Target Workspace', 'type': 'select', 'required': true, 'options': [{ 'value': 'workspace1', 'label': 'Workspace 1' }, { 'value': 'workspace2', 'label': 'Workspace 2' }] }] },
    { 'key': 'delete', 'type': 'delete', 'label': 'Delete Agents', 'description': 'Permanently delete selected agents', 'requiresConfirmation': true, 'confirmationMessage': 'This will permanently delete all selected agents and their data.', 'warning': 'This action cannot be undone. All agent data will be permanently deleted.', 'withProgress': true }
  ];

  // Enhanced chart configurations for comprehensive analytics
  const dailyMetrics = performanceMetrics?.dailyMetrics || [];
  const hasPerformanceData = dailyMetrics.length > 0;

  const performanceChartData = {
    'labels': dailyMetrics.map(d => new Date(d['_id']).toLocaleDateString()) || [],
    'datasets': [
      {
        'label': 'Total Calls',
        'data': dailyMetrics.map(d => d['totalCalls']) || [],
        'borderColor': '#7F8BFA',
        'backgroundColor': 'rgba(127, 139, 250, 0.1)',
        'yAxisID': 'y',
        'tension': 0.4
      },
      {
        'label': 'Success Rate (%)',
        'data': dailyMetrics.map(d => (d['successRate'] * 100).toFixed(1)) || [],
        'borderColor': '#120348',
        'backgroundColor': 'rgba(18, 3, 72, 0.1)',
        'yAxisID': 'y1',
        'tension': 0.4
      }
    ]
  };

  // Cost breakdown data from actual analytics
  const breakdown = costAnalytics?.breakdown || {};
  const costBreakdownData = {
    'labels': Object.keys(breakdown).length > 0 ? Object.keys(breakdown) : [],
    'datasets': [{
      'data': Object.values(breakdown).map(provider => parseFloat(provider['cost']) || 0),
      'backgroundColor': ['#7F8BFA', '#120348', '#F7931A'],
      'borderWidth': 2,
      'borderColor': '#fff'
    }]
  };

  // Agent status distribution chart from actual data
  const activeCount = agentStats?.summary?.active || 0;
  const inactiveCount = agentStats?.summary?.inactive || 0;
  const busyCount = agentStats?.summary?.busy || 0;
  const errorCount = agentStats?.summary?.error || 0;
  const totalAgents = activeCount + inactiveCount + busyCount + errorCount;

  const statusDistributionData = {
    'labels': ['Active', 'Inactive', 'Busy', 'Error'],
    'datasets': [{
      'data': [activeCount, inactiveCount, busyCount, errorCount],
      'backgroundColor': ['#138B3F', '#6C757D', '#F7931A', '#EF4D56'],
      'borderWidth': 2,
      'borderColor': '#fff'
    }]
  };

  // Call volume trend chart from actual data
  const callVolumeData = {
    'labels': dailyMetrics.map(d => new Date(d['_id']).toLocaleDateString()) || [],
    'datasets': [{
      'label': 'Call Volume',
      'data': dailyMetrics.map(d => d['totalCalls']) || [],
      'backgroundColor': 'rgba(127, 139, 250, 0.8)',
      'borderColor': '#7F8BFA',
      'borderWidth': 2,
      'borderRadius': 4
    }]
  };

  // Success rate trends from actual data
  const successRateTrends = {
    'labels': dailyMetrics.map(d => new Date(d['_id']).toLocaleDateString()) || [],
    'datasets': [{
      'label': 'Success Rate',
      'data': dailyMetrics.map(d => (d['successRate'] * 100).toFixed(1)) || [],
      'borderColor': '#138B3F',
      'backgroundColor': 'rgba(19, 139, 63, 0.1)',
      'tension': 0.4,
      'fill': true
    }]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true
      }
    }
  };

  // Prepare hourly activity heatmap data
  const prepareHeatmapData = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const heatmapData = [];

    if (performanceMetrics?.hourlyActivity) {
      performanceMetrics.hourlyActivity.forEach(item => {
        heatmapData.push({
          x: hours[item._id.hour],
          y: days[item._id.dayOfWeek - 1],
          v: item.count,
          successRate: (item.successRate * 100).toFixed(1)
        });
      });
    }

    return heatmapData;
  };

  // Response time distribution data from actual metrics
  const responseTimeData = {
    labels: ['< 1s', '1-3s', '3-5s', '5-10s', '> 10s'],
    datasets: [{
      label: 'Response Time Distribution',
      data: performanceMetrics?.responseTimeDistribution || [0, 0, 0, 0, 0],
      backgroundColor: 'rgba(127, 139, 250, 0.8)',
      borderColor: '#7F8BFA',
      borderWidth: 2
    }]
  };

  // Workspace utilization data from actual analytics
  const workspaceCosts = costAnalytics?.workspaceCosts || [];
  const workspaceUtilizationData = {
    labels: workspaceCosts.map(w => w.workspaceName) || [],
    datasets: [{
      label: 'Calls',
      data: workspaceCosts.map(w => w.calls) || [],
      backgroundColor: 'rgba(127, 139, 250, 0.8)',
      borderColor: '#7F8BFA',
      yAxisID: 'y',
    }, {
      label: 'Cost ($)',
      data: workspaceCosts.map(w => parseFloat(w.cost)) || [],
      backgroundColor: 'rgba(247, 147, 26, 0.8)',
      borderColor: '#F7931A',
      yAxisID: 'y1',
    }]
  };

  // Manual refresh function
  const handleManualRefresh = () => {
    dispatch(getAgentStats());
    dispatch(getPerformanceMetrics({ 'period': '7d' }));
    dispatch(getCostAnalytics({ 'period': '30d' }));
    setLastUpdate(new Date());
  };

  // Performance tab content
  const renderPerformanceTab = () => {
    if (!agentPerformance) return <div className="text-center">Loading performance data...</div>;

    // Chart options for daily metrics
    const dailyChartOptions = {
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: 'Total Calls'
          },
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Success Rate (%)'
          },
          grid: {
            drawOnChartArea: false,
          },
          min: 0,
          max: 100,
        },
        y2: {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Duration (min)'
          },
          grid: {
            drawOnChartArea: false,
          },
          min: 0,
        }
      },
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
        },
      },
    };

    // Chart data for top performers bar chart
    const topPerformersChartData = {
      labels: agentPerformance.topPerformers?.slice(0, 8).map(agent => agent.name) || [],
      datasets: [
        {
          label: 'Success Rate (%)',
          data: agentPerformance.topPerformers?.slice(0, 8).map(agent => agent.successRate) || [],
          backgroundColor: 'var(--primary-color)',
          borderColor: 'var(--primary-color)',
          borderWidth: 2,
          borderRadius: 4,
        },
        {
          label: 'Total Calls',
          data: agentPerformance.topPerformers?.slice(0, 8).map(agent => agent.totalCalls) || [],
          backgroundColor: '#F7931A',
          borderColor: '#F7931A',
          borderWidth: 2,
          borderRadius: 4,
          yAxisID: 'y1'
        }
      ]
    };

    const topPerformersChartOptions = {
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: 'Success Rate (%)'
          },
          min: 0,
          max: 100
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Total Calls'
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Top Performing Agents'
        },
      },
    };

    // Performance distribution chart using backend data
    const performanceDistributionData = {
      labels: ['Excellent (90%+)', 'Good (70-89%)', 'Average (50-69%)', 'Below Average (30-49%)', 'Poor (<30%)'],
      datasets: [{
        data: [
          agentPerformance.performanceDistribution?.excellent || 0,
          agentPerformance.performanceDistribution?.good || 0,
          agentPerformance.performanceDistribution?.average || 0,
          agentPerformance.performanceDistribution?.belowAverage || 0,
          agentPerformance.performanceDistribution?.poor || 0
        ],
        backgroundColor: ['#120348', '#7F8BFA', '#94C8FB', '#F7931A', '#EF4D56'],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    const performanceDistributionOptions = {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom',
        },
        title: {
          display: true,
        },
      },
    };

    return (
      <div>
        {/* Summary Cards */}
        <div className='counter-sections mt-3 mb-4'>
          <div className='counter-box info-box'>
            <div className='counter-start'>
              <div className='left-text-area'>
                <h5>Total Calls</h5>
                <div className='CountUp-box'>
                  <h3>{agentPerformance.summary?.totalCalls?.toLocaleString() || 0}</h3>
                </div>
                <small className="text-muted">This period</small>
              </div>
              <div className='right-icon'>
                <span className='icon'>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle opacity="0.1" cx="16" cy="16" r="16" fill="#007EE5" />
                    <path d="M19.6335 23.1667C18.8802 23.1667 18.0868 22.9867 17.2668 22.64C16.4668 22.3 15.6602 21.8334 14.8735 21.2667C14.0935 20.6934 13.3402 20.0534 12.6268 19.3534C11.9202 18.64 11.2802 17.8867 10.7135 17.1134C10.1402 16.3134 9.68016 15.5134 9.3535 14.74C9.00683 13.9134 8.8335 13.1134 8.8335 12.36C8.8335 11.84 8.92683 11.3467 9.10683 10.8867C9.2935 10.4134 9.5935 9.97337 10.0002 9.59337C10.5135 9.08671 11.1002 8.83337 11.7268 8.83337C11.9868 8.83337 12.2535 8.89337 12.4802 9.00004C12.7402 9.12004 12.9602 9.30004 13.1202 9.54004L14.6668 11.72C14.8068 11.9134 14.9135 12.1 14.9868 12.2867C15.0735 12.4867 15.1202 12.6867 15.1202 12.88C15.1202 13.1334 15.0468 13.38 14.9068 13.6134C14.8068 13.7934 14.6535 13.9867 14.4602 14.18L14.0068 14.6534C14.0135 14.6734 14.0202 14.6867 14.0268 14.7C14.1068 14.84 14.2668 15.08 14.5735 15.44C14.9002 15.8134 15.2068 16.1534 15.5135 16.4667C15.9068 16.8534 16.2335 17.16 16.5402 17.4134C16.9202 17.7334 17.1668 17.8934 17.3135 17.9667L17.3002 18L17.7868 17.52C17.9935 17.3134 18.1935 17.16 18.3868 17.06C18.7535 16.8334 19.2202 16.7934 19.6868 16.9867C19.8602 17.06 20.0468 17.16 20.2468 17.3L22.4602 18.8734C22.7068 19.04 22.8868 19.2534 22.9935 19.5067C23.0935 19.76 23.1402 19.9934 23.1402 20.2267C23.1402 20.5467 23.0668 20.8667 22.9268 21.1667C22.7868 21.4667 22.6135 21.7267 22.3935 21.9667C22.0135 22.3867 21.6002 22.6867 21.1202 22.88C20.6602 23.0667 20.1602 23.1667 19.6335 23.1667ZM11.7268 9.83337C11.3602 9.83337 11.0202 9.99337 10.6935 10.3134C10.3868 10.6 10.1735 10.9134 10.0402 11.2534C9.90016 11.6 9.8335 11.9667 9.8335 12.36C9.8335 12.98 9.98016 13.6534 10.2735 14.3467C10.5735 15.0534 10.9935 15.7867 11.5268 16.52C12.0602 17.2534 12.6668 17.9667 13.3335 18.64C14.0002 19.3 14.7202 19.9134 15.4602 20.4534C16.1802 20.98 16.9202 21.4067 17.6535 21.7134C18.7935 22.2 19.8602 22.3134 20.7402 21.9467C21.0802 21.8067 21.3802 21.5934 21.6535 21.2867C21.8068 21.12 21.9268 20.94 22.0268 20.7267C22.1068 20.56 22.1468 20.3867 22.1468 20.2134C22.1468 20.1067 22.1268 20 22.0735 19.88C22.0535 19.84 22.0135 19.7667 21.8868 19.68L19.6735 18.1067C19.5402 18.0134 19.4202 17.9467 19.3068 17.9C19.1602 17.84 19.1002 17.78 18.8735 17.92C18.7402 17.9867 18.6202 18.0867 18.4868 18.22L17.9802 18.72C17.7202 18.9734 17.3202 19.0334 17.0135 18.92L16.8335 18.84C16.5602 18.6934 16.2402 18.4667 15.8868 18.1667C15.5668 17.8934 15.2202 17.5734 14.8002 17.16C14.4735 16.8267 14.1468 16.4734 13.8068 16.08C13.4935 15.7134 13.2668 15.4 13.1268 15.14L13.0468 14.94C13.0068 14.7867 12.9935 14.7 12.9935 14.6067C12.9935 14.3667 13.0802 14.1534 13.2468 13.9867L13.7468 13.4667C13.8802 13.3334 13.9802 13.2067 14.0468 13.0934C14.1002 13.0067 14.1202 12.9334 14.1202 12.8667C14.1202 12.8134 14.1002 12.7334 14.0668 12.6534C14.0202 12.5467 13.9468 12.4267 13.8535 12.3L12.3068 10.1134C12.2402 10.02 12.1602 9.95337 12.0602 9.90671C11.9535 9.86004 11.8402 9.83337 11.7268 9.83337ZM17.3002 18.0067L17.1935 18.46L17.3735 17.9934C17.3402 17.9867 17.3135 17.9934 17.3002 18.0067Z" fill="#007EE5" />
                  </svg>

                </span>
              </div>
            </div>
          </div>
          <div className='counter-box success-box'>
            <div className='counter-start'>
              <div className='left-text-area'>
                <h5>Avg Success Rate</h5>
                <div className='CountUp-box'>
                  <h3>{agentPerformance.summary?.averageSuccessRate || 0}%</h3>
                </div>
                <small className="text-muted">Across all agents</small>
              </div>
              <div className='right-icon'>
                <span className='icon'>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle opacity="0.1" cx="16" cy="16" r="16" fill="#138B3F" />
                    <path d="M19.6335 23.1667C18.8802 23.1667 18.0868 22.9867 17.2668 22.64C16.4668 22.3 15.6602 21.8334 14.8735 21.2667C14.0935 20.6934 13.3402 20.0534 12.6268 19.3534C11.9202 18.64 11.2802 17.8867 10.7135 17.1134C10.1402 16.3134 9.68016 15.5134 9.3535 14.74C9.00683 13.9134 8.8335 13.1134 8.8335 12.36C8.8335 11.84 8.92683 11.3467 9.10683 10.8867C9.2935 10.4134 9.5935 9.97337 10.0002 9.59337C10.5135 9.08671 11.1002 8.83337 11.7268 8.83337C11.9868 8.83337 12.2535 8.89337 12.4802 9.00004C12.7402 9.12004 12.9602 9.30004 13.1202 9.54004L14.6668 11.72C14.8068 11.9134 14.9135 12.1 14.9868 12.2867C15.0735 12.4867 15.1202 12.6867 15.1202 12.88C15.1202 13.1334 15.0468 13.38 14.9068 13.6134C14.8068 13.7934 14.6535 13.9867 14.4602 14.18L14.0068 14.6534C14.0135 14.6734 14.0202 14.6867 14.0268 14.7C14.1068 14.84 14.2668 15.08 14.5735 15.44C14.9002 15.8134 15.2068 16.1534 15.5135 16.4667C15.9068 16.8534 16.2335 17.16 16.5402 17.4134C16.9202 17.7334 17.1668 17.8934 17.3135 17.9667L17.3002 18L17.7868 17.52C17.9935 17.3134 18.1935 17.16 18.3868 17.06C18.7535 16.8334 19.2202 16.7934 19.6868 16.9867C19.8602 17.06 20.0468 17.16 20.2468 17.3L22.4602 18.8734C22.7068 19.04 22.8868 19.2534 22.9935 19.5067C23.0935 19.76 23.1402 19.9934 23.1402 20.2267C23.1402 20.5467 23.0668 20.8667 22.9268 21.1667C22.7868 21.4667 22.6135 21.7267 22.3935 21.9667C22.0135 22.3867 21.6002 22.6867 21.1202 22.88C20.6602 23.0667 20.1602 23.1667 19.6335 23.1667ZM11.7268 9.83337C11.3602 9.83337 11.0202 9.99337 10.6935 10.3134C10.3868 10.6 10.1735 10.9134 10.0402 11.2534C9.90016 11.6 9.8335 11.9667 9.8335 12.36C9.8335 12.98 9.98016 13.6534 10.2735 14.3467C10.5735 15.0534 10.9935 15.7867 11.5268 16.52C12.0602 17.2534 12.6668 17.9667 13.3335 18.64C14.0002 19.3 14.7202 19.9134 15.4602 20.4534C16.1802 20.98 16.9202 21.4067 17.6535 21.7134C18.7935 22.2 19.8602 22.3134 20.7402 21.9467C21.0802 21.8067 21.3802 21.5934 21.6535 21.2867C21.8068 21.12 21.9268 20.94 22.0268 20.7267C22.1068 20.56 22.1468 20.3867 22.1468 20.2134C22.1468 20.1067 22.1268 20 22.0735 19.88C22.0535 19.84 22.0135 19.7667 21.8868 19.68L19.6735 18.1067C19.5402 18.0134 19.4202 17.9467 19.3068 17.9C19.1602 17.84 19.1002 17.78 18.8735 17.92C18.7402 17.9867 18.6202 18.0867 18.4868 18.22L17.9802 18.72C17.7202 18.9734 17.3202 19.0334 17.0135 18.92L16.8335 18.84C16.5602 18.6934 16.2402 18.4667 15.8868 18.1667C15.5668 17.8934 15.2202 17.5734 14.8002 17.16C14.4735 16.8267 14.1468 16.4734 13.8068 16.08C13.4935 15.7134 13.2668 15.4 13.1268 15.14L13.0468 14.94C13.0068 14.7867 12.9935 14.7 12.9935 14.6067C12.9935 14.3667 13.0802 14.1534 13.2468 13.9867L13.7468 13.4667C13.8802 13.3334 13.9802 13.2067 14.0468 13.0934C14.1002 13.0067 14.1202 12.9334 14.1202 12.8667C14.1202 12.8134 14.1002 12.7334 14.0668 12.6534C14.0202 12.5467 13.9468 12.4267 13.8535 12.3L12.3068 10.1134C12.2402 10.02 12.1602 9.95337 12.0602 9.90671C11.9535 9.86004 11.8402 9.83337 11.7268 9.83337ZM17.3002 18.0067L17.1935 18.46L17.3735 17.9934C17.3402 17.9867 17.3135 17.9934 17.3002 18.0067Z" fill="#138B3F" />
                  </svg>

                </span>
              </div>
            </div>
          </div>
          <div className='counter-box warning-box'>
            <div className='counter-start'>
              <div className='left-text-area'>
                <h5>Total Cost</h5>
                <div className='CountUp-box'>
                  <h3>${agentPerformance.summary?.totalCost?.toLocaleString() || 0}</h3>
                </div>
                <small className="text-muted">This period</small>
              </div>
              <div className='right-icon'>
                <span className='icon'>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle opacity="0.1" cx="16" cy="16" r="16" fill="#DF9524" />
                    <path d="M16.9334 19.6134H15.2601C14.1668 19.6134 13.2801 18.6934 13.2801 17.5601C13.2801 17.2867 13.5068 17.0601 13.7801 17.0601C14.0534 17.0601 14.2801 17.2867 14.2801 17.5601C14.2801 18.1401 14.7201 18.6134 15.2601 18.6134H16.9334C17.3668 18.6134 17.7268 18.2267 17.7268 17.7601C17.7268 17.1801 17.5201 17.0667 17.1801 16.9467L14.4934 16.0001C13.9734 15.8201 13.2734 15.4334 13.2734 14.2401C13.2734 13.2134 14.0801 12.3867 15.0668 12.3867H16.7401C17.8334 12.3867 18.7201 13.3067 18.7201 14.4401C18.7201 14.7134 18.4934 14.9401 18.2201 14.9401C17.9468 14.9401 17.7201 14.7134 17.7201 14.4401C17.7201 13.8601 17.2801 13.3867 16.7401 13.3867H15.0668C14.6334 13.3867 14.2734 13.7734 14.2734 14.2401C14.2734 14.8201 14.4801 14.9334 14.8201 15.0534L17.5068 16.0001C18.0268 16.1801 18.7268 16.5667 18.7268 17.7601C18.7201 18.7801 17.9201 19.6134 16.9334 19.6134Z" fill="#DF9524" />
                    <path d="M16 20.5C15.7267 20.5 15.5 20.2733 15.5 20V12C15.5 11.7267 15.7267 11.5 16 11.5C16.2733 11.5 16.5 11.7267 16.5 12V20C16.5 20.2733 16.2733 20.5 16 20.5Z" fill="#DF9524" />
                    <path d="M16.0002 23.1667C12.0468 23.1667 8.8335 19.9534 8.8335 16C8.8335 12.0467 12.0468 8.83337 16.0002 8.83337C19.9535 8.83337 23.1668 12.0467 23.1668 16C23.1668 19.9534 19.9535 23.1667 16.0002 23.1667ZM16.0002 9.83337C12.6002 9.83337 9.8335 12.6 9.8335 16C9.8335 19.4 12.6002 22.1667 16.0002 22.1667C19.4002 22.1667 22.1668 19.4 22.1668 16C22.1668 12.6 19.4002 9.83337 16.0002 9.83337Z" fill="#DF9524" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div className='counter-box primery-box'>
            <div className='counter-start'>
              <div className='left-text-area'>
                <h5>Avg Duration</h5>
                <div className='CountUp-box'>
                  <h3 style={{ color: '#20808D' }}>{agentPerformance.summary?.averageDuration || 0}m</h3>
                </div>
                <small className="text-muted">Per call</small>
              </div>
              <div className='right-icon'>
                <span className='icon'>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle opacity="0.1" cx="16" cy="16" r="16" fill="#20808D" />
                    <path d="M15.9998 23.1667C12.5065 23.1667 9.6665 20.3267 9.6665 16.8333C9.6665 13.34 12.5065 10.5 15.9998 10.5C19.4932 10.5 22.3332 13.34 22.3332 16.8333C22.3332 20.3267 19.4932 23.1667 15.9998 23.1667ZM15.9998 11.5C13.0598 11.5 10.6665 13.8933 10.6665 16.8333C10.6665 19.7733 13.0598 22.1667 15.9998 22.1667C18.9398 22.1667 21.3332 19.7733 21.3332 16.8333C21.3332 13.8933 18.9398 11.5 15.9998 11.5Z" fill="#20808D" />
                    <path d="M16 17.1667C15.7267 17.1667 15.5 16.94 15.5 16.6667V13.3334C15.5 13.06 15.7267 12.8334 16 12.8334C16.2733 12.8334 16.5 13.06 16.5 13.3334V16.6667C16.5 16.94 16.2733 17.1667 16 17.1667Z" fill="#20808D" />
                    <path d="M18 9.83337H14C13.7267 9.83337 13.5 9.60671 13.5 9.33337C13.5 9.06004 13.7267 8.83337 14 8.83337H18C18.2733 8.83337 18.5 9.06004 18.5 9.33337C18.5 9.60671 18.2733 9.83337 18 9.83337Z" fill="#20808D" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Charts Row */}
        <div className='chart-box'>
          <div className='row'>
            <div className='col-lg-8 col-md-12'>
              <div className='chart-area'>
                <h4>Daily Performance Metrics (Last 7 Days)</h4>
                <div style={{ height: '400px' }}>
                  {agentPerformance.chartData && (
                    <Line data={agentPerformance.chartData} options={dailyChartOptions} />
                  )}
                </div>
              </div>
            </div>
            <div className='col-lg-4 col-md-12'>
              <div className='chart-area'>
                <h4>Performance Distribution</h4>
                <div style={{ height: '400px' }}>
                  <Doughnut data={performanceDistributionData} options={performanceDistributionOptions} />
                </div>
              </div>
            </div>


            <div className='col-lg-8 col-md-12'>
              <div className='chart-area'>
                <h4>Top Performing Agents</h4>
                <div style={{ height: '400px' }}>
                  {agentPerformance.topPerformers && agentPerformance.topPerformers.length > 0 ? (
                    <Bar data={topPerformersChartData} options={topPerformersChartOptions} />
                  ) : (
                    <div className="text-center text-muted">
                      <i className="fa fa-chart-bar fa-3x mb-3"></i>
                      <p>No performance data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='col-lg-4 col-md-12'>
              <div className='chart-area'>
                <h4>Top Performers Details</h4>
                <div style={{ height: '400px' }}>
                  {agentPerformance.topPerformers && agentPerformance.topPerformers.length > 0 ? (
                    <div className="top-performers-list">
                      {agentPerformance.topPerformers.slice(0, 8).map((agent, index) => (
                        <div key={agent.agentId} className="performer-item d-flex align-items-center mb-3 p-2 border rounded">
                          <div className="performer-rank mr-3">
                            <Badge color={index === 0 ? 'warning' : index === 1 ? 'secondary' : index === 2 ? 'danger' : 'light'}>
                              #{index + 1}
                            </Badge>
                          </div>
                          <div className="performer-info flex-grow-1">
                            <div className="performer-name fw-bold">{agent.name}</div>
                            <div className="performer-stats text-muted small">
                              {agent.totalCalls} calls • {agent.successRate.toFixed(1)}% success rate
                            </div>
                            <div className="performer-details text-muted small">
                              Avg Duration: {Math.floor(agent.averageDuration / 60)}m • Cost: ${agent.totalCost?.toFixed(2) || 0}
                            </div>
                          </div>
                          <div className="performer-score text-end">
                            <div className="score-value fw-bold">{agent.successRate.toFixed(1)}%</div>
                            <div className="score-label text-muted small">Success Rate</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted">
                      <i className="fa fa-trophy fa-3x mb-3"></i>
                      <p>No performance data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>

        </div>
        {/* Performance Metrics Table */}
        <div className='content-dashboard'>
          <div className='title-area style-workshop'>
            <h4>Detailed Performance Metrics</h4>
          </div>
          <div className='table-area'>
            {agentPerformance.topPerformers && agentPerformance.topPerformers.length > 0 ? (
              <Table responsive hover className="performance-metrics-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Agent Name</th>
                    <th>Total Calls</th>
                    <th>Successful</th>
                    <th>Failed</th>
                    <th>Success Rate</th>
                    <th>Avg Duration</th>
                    <th>Duration Range</th>
                    <th>Total Cost</th>
                    <th>Cost/Call</th>
                    <th>Performance Score</th>
                  </tr>
                </thead>
                <tbody>
                  {agentPerformance.topPerformers.map((agent, index) => (
                    <tr key={agent.agentId}>
                      <td>
                        <Badge color={index === 0 ? 'warning' : index === 1 ? 'secondary' : index === 2 ? 'danger' : 'light'}>
                          #{index + 1}
                        </Badge>
                      </td>
                      <td>
                        <strong>{agent.name}</strong>
                        <br />
                        <small className="text-muted">{agent.type || 'Voice Agent'}</small>
                      </td>
                      <td>{agent.totalCalls.toLocaleString()}</td>
                      <td>
                        <span className="text-success">{agent.successfulCalls.toLocaleString()}</span>
                      </td>
                      <td>
                        <span className="text-danger">{agent.failedCalls?.toLocaleString() || 0}</span>
                      </td>
                      <td>
                        <Badge color={agent.successRate >= 90 ? 'success' : agent.successRate >= 70 ? 'info' : agent.successRate >= 50 ? 'warning' : 'danger'}>
                          {agent.successRate.toFixed(1)}%
                        </Badge>
                      </td>
                      <td>{Math.floor(agent.averageDuration / 60)}m {Math.floor(agent.averageDuration % 60)}s</td>
                      <td>
                        <small className="text-muted">
                          {Math.floor((agent.minDuration || 0) / 60)}m - {Math.floor((agent.maxDuration || 0) / 60)}m
                        </small>
                      </td>
                      <td>${agent.totalCost?.toFixed(2) || 0}</td>
                      <td>${agent.avgCostPerCall?.toFixed(4) || 0}</td>
                      <td>
                        <Progress
                          value={agent.successRate}
                          color={agent.successRate >= 90 ? 'success' : agent.successRate >= 70 ? 'info' : agent.successRate >= 50 ? 'warning' : 'danger'}
                          className="mb-0"
                        />
                        <small className="text-muted">{agent.successRate.toFixed(1)}%</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="text-center text-muted">
                <i className="fa fa-table fa-3x mb-3"></i>
                <p>No performance data available</p>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  };

  if (loading) return <Container fluid className="mt-4"><div className="text-center">Loading agents management...</div></Container>;

  return (
    <>
      <div className='content-dashboard'>
        <div className='main-title'>
          <div className='left-area'>
            <h3>AI Agents Management</h3>
            <p className="text-muted mb-3">Monitor and manage voice AI agents</p>
          </div>
          <div className='right-area'>
            <div className='btn-group-right'>
              {/* <button className='btn-style-new'
                onClick={() => {
                  dispatch(getAgentStats());
                  dispatch(getAdminAgents({ 'page': currentPage, 'search': searchTerm, 'status': statusFilter }));
                  dispatch(getPerformanceMetrics({ 'period': '7d' }));
                  dispatch(getCostAnalytics({ 'period': '30d' }));
                  if (activeTab === 'performance') {
                    dispatch(getAgentPerformance({ 'period': '7d' }));
                  }
                }}
              >
                <i className="icon fa fa-undo"></i> Refresh
              </button> */}

              {/* Refresh Controls */}
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <button className='btn-style-new' onClick={handleManualRefresh}>
                    <i className="icon fa fa-undo"></i> Refresh
                  </button>
                  <div className="auto-refresh-input">
                    <Checkbox checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                    <span>Auto-refresh</span>
                  </div>
                </div>
              </div>
              <UncontrolledDropdown>
                <DropdownToggle size="sm" color="success" caret>
                  <i className="fa fa-cog mr-1"></i> Actions
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={() => handleAgentAction('all', 'restart')}>
                    <i className="fa fa-refresh mr-2"></i> Restart All
                  </DropdownItem>
                  <DropdownItem onClick={() => handleAgentAction('all', 'stop')}>
                    <i className="fa fa-stop mr-2"></i> Stop All
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
            <small className="text-muted">Last updated: {lastUpdate.toLocaleTimeString()}</small>
          </div>
        </div>
        <div className='tabs-area'>
          <div className='tab-header'>
            {/* Navigation Tabs */}
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={activeTab === 'overview' ? 'active' : ''}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'agent-list' ? 'active' : ''}
                  onClick={() => setActiveTab('agent-list')}
                >
                  Agent List
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'performance' ? 'active' : ''}
                  onClick={() => setActiveTab('performance')}
                >
                  Performance
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'cost-analytics' ? 'active' : ''}
                  onClick={() => setActiveTab('cost-analytics')}
                >
                  Cost Analytics
                </NavLink>
              </NavItem>
            </Nav>
          </div>

          <TabContent activeTab={activeTab}>
            {/* Overview Tab */}
            <TabPane tabId="overview">
              <div className='counter-sections mt-3'>
                <div className='counter-box info-box'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Total Agents</h5>
                      <div className='CountUp-box'>
                        <h3>{agentStats?.summary?.['total'] || agentStats?.['total'] || 0}</h3>
                      </div>
                    </div>
                    <div className='right-icon'>
                      <span className='icon'><svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle opacity="0.1" cx="16" cy="16" r="16" fill="#007EE5" />
                        <g clip-path="url(#clip0_3616_3822)">
                          <path d="M23.0627 19.7755C22.9828 20.6312 22.7128 21.4115 22.2025 22.0948C21.2926 23.3123 20.0744 23.9671 18.5515 23.9859C16.7717 24.0076 14.9913 24.0007 13.2109 23.9888C10.8779 23.9733 8.94798 22.2032 8.70481 19.8777C8.70138 19.8434 8.69168 19.8098 8.68597 19.7795C7.63395 19.5706 7.0557 19.0146 7.00376 18.1607C6.9735 17.668 7.12591 17.2354 7.46042 16.87C7.78921 16.511 8.21105 16.346 8.69054 16.3083C8.76417 15.4801 9.01534 14.73 9.48798 14.0673C10.411 12.7738 11.6771 12.1122 13.2651 12.0825C13.9341 12.07 14.6037 12.0803 15.2733 12.0803C15.3509 12.0803 15.4285 12.0803 15.5375 12.0803C15.5375 11.8542 15.5375 11.643 15.5375 11.4318C15.5375 11.083 15.5318 10.7337 15.5404 10.3849C15.5438 10.2553 15.5084 10.1891 15.3834 10.1281C14.8931 9.88945 14.6551 9.36087 14.7846 8.84142C14.9091 8.34366 15.3629 7.9966 15.8852 8.00003C16.4143 8.00345 16.8522 8.35793 16.9743 8.88137C17.0896 9.37571 16.8436 9.88203 16.3732 10.1332C16.3133 10.1652 16.2659 10.2262 16.2248 10.2827C16.2077 10.3061 16.2214 10.3518 16.2214 10.3878C16.2214 10.942 16.2214 11.4957 16.2214 12.0797C16.3207 12.0797 16.4035 12.0791 16.4857 12.0797C17.3042 12.0888 18.1251 12.062 18.9408 12.1139C20.9758 12.2447 22.7733 13.9748 23.0233 16.0012C23.0353 16.0994 23.0513 16.1976 23.065 16.2906C24.2654 16.6183 24.7929 17.1903 24.746 18.1059C24.7015 19.0129 24.1056 19.61 23.0627 19.7755ZM15.8743 23.3078C16.7357 23.3078 17.5971 23.3123 18.4584 23.3066C20.6801 23.2923 22.376 21.5959 22.3863 19.3794C22.3903 18.4826 22.392 17.5853 22.3834 16.6885C22.3811 16.4265 22.364 16.1599 22.3109 15.9042C21.925 14.033 20.3656 12.7715 18.431 12.7641C16.8008 12.7578 15.1705 12.7618 13.5402 12.7641C13.3056 12.7647 13.0705 12.7744 12.837 12.7926C11.1085 12.9302 9.52736 14.4429 9.40749 16.1731C9.32757 17.321 9.37667 18.4775 9.3721 19.6305C9.37153 19.7647 9.39037 19.9005 9.41491 20.0335C9.7711 21.992 11.3369 23.3009 13.333 23.3072C14.1796 23.3106 15.0267 23.3078 15.8743 23.3078ZM23.0872 17.0327C23.0872 17.7086 23.0872 18.385 23.0872 19.062C23.6883 19.022 24.1107 18.5357 24.0622 17.9557C24.0183 17.422 23.5644 16.9905 23.0872 17.0327ZM8.65629 19.0643C8.65629 18.3736 8.65629 17.6972 8.65629 17.0202C8.11743 17.0413 7.69788 17.4757 7.68418 18.0083C7.67048 18.5637 8.07519 19.0095 8.65629 19.0643ZM15.8726 8.68558C15.6317 8.6873 15.4422 8.8751 15.4394 9.11541C15.4365 9.36429 15.6323 9.55951 15.8823 9.55666C16.1244 9.55437 16.3116 9.36772 16.315 9.12683C16.3179 8.87795 16.1238 8.68387 15.8726 8.68558Z" fill="#007EE5" stroke="#007EE5" stroke-width="0.08" />
                          <path d="M15.8669 21.3126C14.9273 21.3126 13.9877 21.3144 13.0482 21.3121C11.6274 21.3092 10.6775 20.3599 10.6758 18.9426C10.6753 18.3232 10.6736 17.7039 10.6764 17.0846C10.6821 15.726 11.6502 14.7613 13.0094 14.7607C14.917 14.7596 16.8247 14.7596 18.733 14.7607C20.1001 14.7613 21.0585 15.7095 21.0677 17.0771C21.0722 17.789 21.0819 18.5013 21.0574 19.2126C21.0197 20.32 20.0893 21.267 18.9859 21.2978C17.9475 21.3269 16.9075 21.3041 15.868 21.3041C15.8669 21.3069 15.8669 21.3098 15.8669 21.3126ZM15.8623 20.6288C16.8304 20.6288 17.798 20.6334 18.7655 20.6277C19.7239 20.6214 20.373 19.9752 20.3821 19.0208C20.3884 18.3661 20.3878 17.7113 20.3821 17.0566C20.3735 16.0936 19.7131 15.4452 18.7467 15.4446C16.8322 15.4434 14.9182 15.4434 13.0036 15.4446C12.0224 15.4452 11.3625 16.109 11.3597 17.0965C11.358 17.7085 11.3591 18.3204 11.3597 18.9323C11.3603 19.9803 12.0007 20.6259 13.0447 20.6277C13.9838 20.6299 14.9228 20.6288 15.8623 20.6288Z" fill="#007EE5" stroke="#007EE5" stroke-width="0.08" />
                          <path d="M13.6719 19.4678C12.8825 19.4644 12.25 18.8291 12.25 18.039C12.25 17.2416 12.8956 16.5966 13.6896 16.6017C14.4728 16.6069 15.1144 17.2519 15.1155 18.035C15.1161 18.8308 14.4711 19.4707 13.6719 19.4678ZM13.6531 18.7828C14.0772 18.7942 14.4248 18.4666 14.4311 18.0487C14.4374 17.6406 14.1069 17.297 13.6982 17.2861C13.2912 17.2753 12.9435 17.6075 12.9344 18.0151C12.9253 18.4329 13.2421 18.7714 13.6531 18.7828Z" fill="#007EE5" stroke="#007EE5" stroke-width="0.08" />
                          <path d="M18.0768 19.4678C17.2771 19.4706 16.6372 18.8308 16.6406 18.0322C16.6441 17.2444 17.2817 16.6045 18.066 16.6017C18.8554 16.5988 19.5124 17.2553 19.5079 18.0424C19.5039 18.8285 18.8646 19.4649 18.0768 19.4678ZM18.0734 18.7828C18.485 18.7817 18.8212 18.4489 18.8229 18.0402C18.8246 17.628 18.4707 17.2781 18.058 17.2861C17.6487 17.2935 17.3216 17.6326 17.3251 18.0447C17.3291 18.4609 17.6567 18.7839 18.0734 18.7828Z" fill="#007EE5" stroke="#007EE5" stroke-width="0.08" />
                        </g>
                        <defs>
                          <clipPath id="clip0_3616_3822">
                            <rect width="18" height="18" fill="white" transform="translate(7 7)" />
                          </clipPath>
                        </defs>
                      </svg>
                      </span>
                    </div>
                  </div>
                </div>
                <div className='counter-box success-box'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Active Agents</h5>
                      <div className='CountUp-box'>
                        <h3 className="text-success">{agentStats?.summary?.['active'] || agentStats?.['active'] || 0}</h3>
                      </div>
                      <Progress
                        value={((agentStats?.summary?.['active'] || agentStats?.['active'] || 0) / (agentStats?.summary?.['total'] || agentStats?.['total'] || 1)) * 100}
                        color="success"
                      />
                    </div>
                    <div className='right-icon'>
                      <span className='icon'>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle opacity="0.1" cx="16" cy="16" r="16" fill="#138B3F" />
                          <path d="M23.1882 19.7755C23.1083 20.6312 22.8383 21.4115 22.328 22.0948C21.4181 23.3123 20.1999 23.9671 18.677 23.9859C16.8972 24.0076 15.1168 24.0007 13.3364 23.9888C11.0034 23.9733 9.07347 22.2032 8.8303 19.8777C8.82687 19.8434 8.81717 19.8098 8.81146 19.7795C7.75943 19.5706 7.18119 19.0146 7.12925 18.1607C7.09899 17.668 7.2514 17.2354 7.5859 16.87C7.9147 16.511 8.33654 16.346 8.81603 16.3083C8.88966 15.4801 9.14082 14.73 9.61346 14.0673C10.5365 12.7738 11.8026 12.1122 13.3906 12.0825C14.0596 12.07 14.7292 12.0803 15.3987 12.0803C15.4764 12.0803 15.554 12.0803 15.663 12.0803C15.663 11.8542 15.663 11.643 15.663 11.4318C15.663 11.083 15.6573 10.7337 15.6659 10.3849C15.6693 10.2553 15.6339 10.1891 15.5089 10.1281C15.0186 9.88945 14.7805 9.36087 14.9101 8.84142C15.0346 8.34366 15.4884 7.9966 16.0107 8.00003C16.5398 8.00345 16.9776 8.35793 17.0998 8.88137C17.2151 9.37571 16.9691 9.88203 16.4987 10.1332C16.4388 10.1652 16.3914 10.2262 16.3503 10.2827C16.3332 10.3061 16.3469 10.3518 16.3469 10.3878C16.3469 10.942 16.3469 11.4957 16.3469 12.0797C16.4462 12.0797 16.529 12.0791 16.6112 12.0797C17.4297 12.0888 18.2506 12.062 19.0663 12.1139C21.1013 12.2447 22.8988 13.9748 23.1488 16.0012C23.1608 16.0994 23.1768 16.1976 23.1905 16.2906C24.3909 16.6183 24.9183 17.1903 24.8715 18.1059C24.827 19.0129 24.2311 19.61 23.1882 19.7755ZM15.9998 23.3078C16.8612 23.3078 17.7226 23.3123 18.5839 23.3066C20.8056 23.2923 22.5015 21.5959 22.5118 19.3794C22.5158 18.4826 22.5175 17.5853 22.5089 16.6885C22.5066 16.4265 22.4895 16.1599 22.4364 15.9042C22.0505 14.033 20.4911 12.7715 18.5565 12.7641C16.9263 12.7578 15.296 12.7618 13.6657 12.7641C13.4311 12.7647 13.1959 12.7744 12.9625 12.7926C11.234 12.9302 9.65285 14.4429 9.53298 16.1731C9.45306 17.321 9.50215 18.4775 9.49759 19.6305C9.49702 19.7647 9.51585 19.9005 9.5404 20.0335C9.89659 21.992 11.4624 23.3009 13.4585 23.3072C14.3051 23.3106 15.1522 23.3078 15.9998 23.3078ZM23.2127 17.0327C23.2127 17.7086 23.2127 18.385 23.2127 19.062C23.8138 19.022 24.2362 18.5357 24.1877 17.9557C24.1437 17.422 23.6899 16.9905 23.2127 17.0327ZM8.78178 19.0643C8.78178 18.3736 8.78178 17.6972 8.78178 17.0202C8.24292 17.0413 7.82337 17.4757 7.80967 18.0083C7.79597 18.5637 8.20068 19.0095 8.78178 19.0643ZM15.9981 8.68558C15.7572 8.6873 15.5677 8.8751 15.5649 9.11541C15.562 9.36429 15.7578 9.55951 16.0078 9.55666C16.2498 9.55437 16.4371 9.36772 16.4405 9.12683C16.4434 8.87795 16.2493 8.68387 15.9981 8.68558Z" fill="#138B3F" stroke="#138B3F" stroke-width="0.08" />
                          <path d="M15.9924 21.3126C15.0528 21.3126 14.1132 21.3144 13.1737 21.3121C11.7529 21.3092 10.803 20.3599 10.8013 18.9426C10.8008 18.3232 10.799 17.7039 10.8019 17.0846C10.8076 15.726 11.7757 14.7613 13.1348 14.7607C15.0425 14.7596 16.9502 14.7596 18.8585 14.7607C20.2256 14.7613 21.184 15.7095 21.1931 17.0771C21.1977 17.789 21.2074 18.5013 21.1829 19.2126C21.1452 20.32 20.2148 21.267 19.1114 21.2978C18.073 21.3269 17.033 21.3041 15.9935 21.3041C15.9924 21.3069 15.9924 21.3098 15.9924 21.3126ZM15.9878 20.6288C16.9559 20.6288 17.9235 20.6334 18.891 20.6277C19.8494 20.6214 20.4985 19.9752 20.5076 19.0208C20.5139 18.3661 20.5133 17.7113 20.5076 17.0566C20.499 16.0936 19.8386 15.4452 18.8722 15.4446C16.9576 15.4434 15.0437 15.4434 13.1291 15.4446C12.1479 15.4452 11.488 16.109 11.4852 17.0965C11.4835 17.7085 11.4846 18.3204 11.4852 18.9323C11.4857 19.9803 12.1262 20.6259 13.1702 20.6277C14.1092 20.6299 15.0482 20.6288 15.9878 20.6288Z" fill="#138B3F" stroke="#138B3F" stroke-width="0.08" />
                          <path d="M13.7974 19.4678C13.008 19.4644 12.3755 18.8291 12.3755 18.039C12.3755 17.2416 13.0211 16.5966 13.8151 16.6017C14.5983 16.6069 15.2399 17.2519 15.241 18.035C15.2416 18.8308 14.5966 19.4707 13.7974 19.4678ZM13.7786 18.7828C14.2027 18.7942 14.5503 18.4666 14.5566 18.0487C14.5629 17.6406 14.2324 17.297 13.8237 17.2861C13.4167 17.2753 13.069 17.6075 13.0599 18.0151C13.0508 18.4329 13.3676 18.7714 13.7786 18.7828Z" fill="#138B3F" stroke="#138B3F" stroke-width="0.08" />
                          <path d="M18.2023 19.4678C17.4026 19.4706 16.7627 18.8308 16.7661 18.0322C16.7696 17.2444 17.4072 16.6045 18.1915 16.6017C18.9809 16.5988 19.6379 17.2553 19.6334 18.0424C19.6294 18.8285 18.9901 19.4649 18.2023 19.4678ZM18.1989 18.7828C18.6105 18.7817 18.9467 18.4489 18.9484 18.0402C18.9501 17.628 18.5962 17.2781 18.1835 17.2861C17.7742 17.2935 17.4471 17.6326 17.4505 18.0447C17.4545 18.4609 17.7822 18.7839 18.1989 18.7828Z" fill="#138B3F" stroke="#138B3F" stroke-width="0.08" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
                <div className='counter-box primery-box'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Today's Calls</h5>
                      <div className='CountUp-box'>
                        <h3>{agentStats?.callStats?.['todaysCalls'] || agentStats?.['callStats']?.['todaysCalls'] || 0}</h3>
                      </div>
                    </div>
                    <div className='right-icon'>
                      <span className='icon'>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle opacity="0.1" cx="16" cy="16" r="16" fill="#7E1FD2" />
                          <path d="M19.6335 23.1666C18.8802 23.1666 18.0868 22.9866 17.2668 22.64C16.4668 22.3 15.6602 21.8333 14.8735 21.2666C14.0935 20.6933 13.3402 20.0533 12.6268 19.3533C11.9202 18.64 11.2802 17.8866 10.7135 17.1133C10.1402 16.3133 9.68016 15.5133 9.3535 14.74C9.00683 13.9133 8.8335 13.1133 8.8335 12.36C8.8335 11.84 8.92683 11.3466 9.10683 10.8866C9.2935 10.4133 9.5935 9.97331 10.0002 9.59331C10.5135 9.08665 11.1002 8.83331 11.7268 8.83331C11.9868 8.83331 12.2535 8.89331 12.4802 8.99998C12.7402 9.11998 12.9602 9.29998 13.1202 9.53998L14.6668 11.72C14.8068 11.9133 14.9135 12.1 14.9868 12.2866C15.0735 12.4866 15.1202 12.6866 15.1202 12.88C15.1202 13.1333 15.0468 13.38 14.9068 13.6133C14.8068 13.7933 14.6535 13.9866 14.4602 14.18L14.0068 14.6533C14.0135 14.6733 14.0202 14.6866 14.0268 14.7C14.1068 14.84 14.2668 15.08 14.5735 15.44C14.9002 15.8133 15.2068 16.1533 15.5135 16.4666C15.9068 16.8533 16.2335 17.16 16.5402 17.4133C16.9202 17.7333 17.1668 17.8933 17.3135 17.9666L17.3002 18L17.7868 17.52C17.9935 17.3133 18.1935 17.16 18.3868 17.06C18.7535 16.8333 19.2202 16.7933 19.6868 16.9866C19.8602 17.06 20.0468 17.16 20.2468 17.3L22.4602 18.8733C22.7068 19.04 22.8868 19.2533 22.9935 19.5066C23.0935 19.76 23.1402 19.9933 23.1402 20.2266C23.1402 20.5466 23.0668 20.8666 22.9268 21.1666C22.7868 21.4666 22.6135 21.7266 22.3935 21.9666C22.0135 22.3866 21.6002 22.6866 21.1202 22.88C20.6602 23.0666 20.1602 23.1666 19.6335 23.1666ZM11.7268 9.83331C11.3602 9.83331 11.0202 9.99331 10.6935 10.3133C10.3868 10.6 10.1735 10.9133 10.0402 11.2533C9.90016 11.6 9.8335 11.9666 9.8335 12.36C9.8335 12.98 9.98016 13.6533 10.2735 14.3466C10.5735 15.0533 10.9935 15.7866 11.5268 16.52C12.0602 17.2533 12.6668 17.9666 13.3335 18.64C14.0002 19.3 14.7202 19.9133 15.4602 20.4533C16.1802 20.98 16.9202 21.4066 17.6535 21.7133C18.7935 22.2 19.8602 22.3133 20.7402 21.9466C21.0802 21.8066 21.3802 21.5933 21.6535 21.2866C21.8068 21.12 21.9268 20.94 22.0268 20.7266C22.1068 20.56 22.1468 20.3866 22.1468 20.2133C22.1468 20.1066 22.1268 20 22.0735 19.88C22.0535 19.84 22.0135 19.7666 21.8868 19.68L19.6735 18.1066C19.5402 18.0133 19.4202 17.9466 19.3068 17.9C19.1602 17.84 19.1002 17.78 18.8735 17.92C18.7402 17.9866 18.6202 18.0866 18.4868 18.22L17.9802 18.72C17.7202 18.9733 17.3202 19.0333 17.0135 18.92L16.8335 18.84C16.5602 18.6933 16.2402 18.4666 15.8868 18.1666C15.5668 17.8933 15.2202 17.5733 14.8002 17.16C14.4735 16.8266 14.1468 16.4733 13.8068 16.08C13.4935 15.7133 13.2668 15.4 13.1268 15.14L13.0468 14.94C13.0068 14.7866 12.9935 14.7 12.9935 14.6066C12.9935 14.3666 13.0802 14.1533 13.2468 13.9866L13.7468 13.4666C13.8802 13.3333 13.9802 13.2066 14.0468 13.0933C14.1002 13.0066 14.1202 12.9333 14.1202 12.8666C14.1202 12.8133 14.1002 12.7333 14.0668 12.6533C14.0202 12.5466 13.9468 12.4266 13.8535 12.3L12.3068 10.1133C12.2402 10.02 12.1602 9.95331 12.0602 9.90665C11.9535 9.85998 11.8402 9.83331 11.7268 9.83331ZM17.3002 18.0066L17.1935 18.46L17.3735 17.9933C17.3402 17.9866 17.3135 17.9933 17.3002 18.0066Z" fill="#7E1FD2" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
                <div className='counter-box warning-box'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Monthly Cost</h5>
                      <div className='CountUp-box'>
                        <h3 className="text-warning">${(costAnalytics?.totalCost || 0).toLocaleString()}</h3>
                      </div>
                    </div>
                    <div className='right-icon'>
                      <span className='icon'>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle opacity="0.1" cx="16" cy="16" r="16" fill="#DF9524" />
                          <path d="M16.9334 19.6133H15.2601C14.1668 19.6133 13.2801 18.6933 13.2801 17.56C13.2801 17.2867 13.5068 17.06 13.7801 17.06C14.0534 17.06 14.2801 17.2867 14.2801 17.56C14.2801 18.14 14.7201 18.6133 15.2601 18.6133H16.9334C17.3668 18.6133 17.7268 18.2267 17.7268 17.76C17.7268 17.18 17.5201 17.0667 17.1801 16.9467L14.4934 16C13.9734 15.82 13.2734 15.4333 13.2734 14.24C13.2734 13.2133 14.0801 12.3867 15.0668 12.3867H16.7401C17.8334 12.3867 18.7201 13.3067 18.7201 14.44C18.7201 14.7133 18.4934 14.94 18.2201 14.94C17.9468 14.94 17.7201 14.7133 17.7201 14.44C17.7201 13.86 17.2801 13.3867 16.7401 13.3867H15.0668C14.6334 13.3867 14.2734 13.7733 14.2734 14.24C14.2734 14.82 14.4801 14.9333 14.8201 15.0533L17.5068 16C18.0268 16.18 18.7268 16.5667 18.7268 17.76C18.7201 18.78 17.9201 19.6133 16.9334 19.6133Z" fill="#DF9524" />
                          <path d="M16 20.5C15.7267 20.5 15.5 20.2733 15.5 20V12C15.5 11.7267 15.7267 11.5 16 11.5C16.2733 11.5 16.5 11.7267 16.5 12V20C16.5 20.2733 16.2733 20.5 16 20.5Z" fill="#DF9524" />
                          <path d="M16.0002 23.1666C12.0468 23.1666 8.8335 19.9533 8.8335 16C8.8335 12.0466 12.0468 8.83331 16.0002 8.83331C19.9535 8.83331 23.1668 12.0466 23.1668 16C23.1668 19.9533 19.9535 23.1666 16.0002 23.1666ZM16.0002 9.83331C12.6002 9.83331 9.8335 12.6 9.8335 16C9.8335 19.4 12.6002 22.1666 16.0002 22.1666C19.4002 22.1666 22.1668 19.4 22.1668 16C22.1668 12.6 19.4002 9.83331 16.0002 9.83331Z" fill="#DF9524" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Analytics Charts */}
              <div className='chart-box-area'>
                {/* First Row - Performance Overview */}
                <div className='row'>
                  <div className='col-lg-8 col-md-12'>
                    <div className='chart-box'>
                      <div className='chart-area' style={{ height: '350px' }}>
                        <h4 className="mb-3">Performance Trends</h4>
                        <Line
                          data={performanceChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            interaction: {
                              mode: 'index',
                              intersect: false,
                            },
                            scales: {
                              y: {
                                type: 'linear',
                                display: true,
                                position: 'left',
                                title: {
                                  display: true,
                                  text: 'Total Calls'
                                },
                              },
                              y1: {
                                type: 'linear',
                                display: true,
                                position: 'right',
                                title: {
                                  display: true,
                                  text: 'Success Rate (%)'
                                },
                                grid: {
                                  drawOnChartArea: false,
                                },
                                min: 0,
                                max: 100,
                              }
                            },
                            plugins: {
                              legend: {
                                position: 'top',
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-4 col-md-12'>
                    <div className='chart-box'>
                      <div className='chart-area' style={{ height: '350px' }}>
                        <h4 className="mb-3">Agent Status Distribution</h4>
                        <Doughnut
                          data={statusDistributionData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'bottom',
                              },
                              tooltip: {
                                callbacks: {
                                  label: function (context) {
                                    const label = context.label || '';
                                    const value = context.parsed || 0;
                                    const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                    const percentage = ((value / total) * 100).toFixed(1);
                                    return `${label}: ${value} (${percentage}%)`;
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Second Row - Call Volume and Success Rate */}
                <div className='row'>
                  <div className='col-lg-6 col-md-12'>
                    <div className='chart-box'>
                      <div className='chart-area' style={{ height: '300px' }}>
                        <h4 className="mb-3">Call Volume Trends</h4>
                        <Bar
                          data={callVolumeData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              y: {
                                beginAtZero: true,
                                title: {
                                  display: true,
                                  text: 'Number of Calls'
                                }
                              }
                            },
                            plugins: {
                              legend: {
                                display: false,
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6 col-md-12'>
                    <div className='chart-box'>
                      <div className='chart-area' style={{ height: '300px' }}>
                        <h4 className="mb-3">Success Rate Trends</h4>
                        <Line
                          data={successRateTrends}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              y: {
                                beginAtZero: true,
                                max: 100,
                                title: {
                                  display: true,
                                  text: 'Success Rate (%)'
                                }
                              }
                            },
                            plugins: {
                              legend: {
                                display: false,
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Third Row - Cost Breakdown and Statistics */}
                <div className='row'>
                  <div className='col-lg-4 col-md-12'>
                    <div className='chart-box'>
                      <div className='chart-area' style={{ height: '300px' }}>
                        <h4 className="mb-3">Cost Breakdown</h4>
                        <Doughnut
                          data={costBreakdownData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'bottom',
                              },
                              tooltip: {
                                callbacks: {
                                  label: function (context) {
                                    const label = context.label || '';
                                    const value = context.parsed || 0;
                                    return `${label}: $${value.toFixed(2)}`;
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-4 col-md-12'>
                    <div className='chart-box'>
                      <div className='chart-area'>
                        <div className='table-area table-workspaces'>
                          <h4 className="mb-3">Call Statistics</h4>
                          <Table size="sm" responsive>
                            <tbody>
                              <tr>
                                <td>Total Calls</td>
                                <td className="text-right font-weight-bold">{agentStats?.callStats?.['totalCalls']?.toLocaleString() || agentStats?.['callStats']?.['totalCalls']?.toLocaleString() || 0}</td>
                              </tr>
                              <tr>
                                <td>Total Duration</td>
                                <td className="text-right">{Math.floor((agentStats?.callStats?.['totalDuration'] || agentStats?.['callStats']?.['totalDuration'] || 0) / 60)} min</td>
                              </tr>
                              <tr>
                                <td>Average Duration</td>
                                <td className="text-right">{Math.floor((agentStats?.callStats?.['averageDuration'] || agentStats?.['callStats']?.['averageDuration'] || 0) / 60)} min</td>
                              </tr>
                              <tr>
                                <td>Today's Calls</td>
                                <td className="text-right text-primary font-weight-bold">{agentStats?.callStats?.['todaysCalls'] || agentStats?.['callStats']?.['todaysCalls'] || 0}</td>
                              </tr>
                              <tr>
                                <td>Growth Rate</td>
                                <td className="text-right">
                                  <Badge color={(agentStats?.summary?.['growth'] || agentStats?.['growth'] || 0) > 0 ? 'success' : 'danger'}>
                                    {(agentStats?.summary?.['growth'] || agentStats?.['growth'] || 0) > 0 ? '+' : ''}{agentStats?.summary?.['growth'] || agentStats?.['growth'] || 0}%
                                  </Badge>
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-4 col-md-12'>
                    <div className='chart-box'>
                      <div className='chart-area'>
                        <div className='table-area table-workspaces'>
                          <h4 className="mb-3">Top Performing Agents</h4>
                          {(performanceMetrics?.topPerformers || []).length > 0 ? (
                            performanceMetrics.topPerformers.slice(0, 5).map((agent, index) => (
                              <div key={agent._id} className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <div>
                                    <strong>#{index + 1} {agent.agent?.name || 'Agent'}</strong>
                                    <br />
                                    <small className="text-muted">Calls: {agent.totalCalls || 0}</small>
                                  </div>
                                  <Badge color="success">{((agent.successRate || 0) * 100).toFixed(0)}%</Badge>
                                </div>
                                <Progress
                                  value={(agent.successRate || 0) * 100}
                                  color={agent.successRate > 0.8 ? 'success' : agent.successRate > 0.6 ? 'warning' : 'danger'}
                                  style={{ height: '8px' }}
                                />
                              </div>
                            ))
                          ) : (
                            <div className="text-center text-muted py-3">
                              <i className="fa fa-chart-bar fa-2x mb-2"></i>
                              <p>No agent performance data available yet</p>
                              <small>Data will appear once agents start making calls</small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fourth Row - Advanced Analytics */}
                <div className='row'>
                  <div className='col-lg-6 col-md-12'>
                    <div className='chart-box'>
                      <div className='chart-area' style={{ height: '300px' }}>
                        <h4 className="mb-3">Response Time Distribution</h4>
                        <Bar
                          data={responseTimeData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              y: {
                                beginAtZero: true,
                                title: {
                                  display: true,
                                  text: 'Number of Calls'
                                }
                              },
                              x: {
                                title: {
                                  display: true,
                                  text: 'Response Time'
                                }
                              }
                            },
                            plugins: {
                              legend: {
                                display: false,
                              },
                              tooltip: {
                                callbacks: {
                                  label: function (context) {
                                    return `Calls: ${context.parsed.y}`;
                                  }
                                }
                              }
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6 col-md-12'>
                    <div className='chart-box'>
                      <div className='chart-area' style={{ height: '300px' }}>
                        <h4 className="mb-3">Workspace Utilization</h4>
                        <Bar
                          data={workspaceUtilizationData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              y: {
                                type: 'linear',
                                display: true,
                                position: 'left',
                                title: {
                                  display: true,
                                  text: 'Number of Calls'
                                },
                                beginAtZero: true
                              },
                              y1: {
                                type: 'linear',
                                display: true,
                                position: 'right',
                                title: {
                                  display: true,
                                  text: 'Cost ($)'
                                },
                                grid: {
                                  drawOnChartArea: false,
                                },
                                beginAtZero: true
                              }
                            },
                            plugins: {
                              legend: {
                                position: 'top',
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fifth Row - Hourly Activity Heatmap */}
                <div className='row'>
                  <div className='col-12'>
                    <div className='chart-box'>
                      <div className='chart-area'>
                        <h4 className="mb-3">Weekly Activity Heatmap</h4>
                        <div className="alert alert-info">
                          <i className="fa fa-info-circle mr-2"></i>
                          Activity patterns show peak hours and days for agent utilization. Darker colors indicate higher activity.
                        </div>
                        {performanceMetrics?.hourlyActivity && (
                          <div className="heatmap-container" style={{ overflowX: 'auto' }}>
                            <table className="table table-bordered table-sm text-center">
                              <thead>
                                <tr>
                                  <th>Day/Hour</th>
                                  {Array.from({ length: 24 }, (_, i) => (
                                    <th key={i} style={{ minWidth: '40px', fontSize: '11px' }}>{i}:00</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, dayIndex) => (
                                  <tr key={day}>
                                    <td><strong>{day}</strong></td>
                                    {Array.from({ length: 24 }, (_, hourIndex) => {
                                      const activity = performanceMetrics.hourlyActivity.find(
                                        item => item._id.dayOfWeek === dayIndex + 1 && item._id.hour === hourIndex
                                      );
                                      const count = activity?.count || 0;
                                      const maxCount = Math.max(...(performanceMetrics.hourlyActivity?.map(a => a.count) || [1]));
                                      const intensity = count / maxCount;
                                      const backgroundColor = count > 0
                                        ? `rgba(127, 139, 250, ${0.2 + intensity * 0.8})`
                                        : 'rgba(0, 0, 0, 0.05)';

                                      return (
                                        <td
                                          key={hourIndex}
                                          style={{
                                            backgroundColor,
                                            color: intensity > 0.5 ? '#fff' : '#333',
                                            fontSize: '11px',
                                            cursor: 'pointer'
                                          }}
                                          title={`${count} calls, ${activity ? (activity.successRate * 100).toFixed(0) : 0}% success rate`}
                                        >
                                          {count || '-'}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sixth Row - Recent Activity Table */}
                <div className='row'>
                  <div className='col-12'>
                    <div className='chart-area'>
                      <h4 className="mb-3">Recent Agent Activity</h4>
                      <div className="table-responsive">
                        <Table hover>
                          <thead>
                            <tr>
                              <th>Agent</th>
                              <th>Workspace</th>
                              <th>Status</th>
                              <th>Last Activity</th>
                              <th>Calls Today</th>
                              <th>Success Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(agentStats?.recentAgents || agentStats?.['recentAgents'] || []).slice(0, 10).map(agent => (
                              <tr key={agent['_id']}>
                                <td><strong>{agent['name']}</strong></td>
                                <td>{agent['workspaceId']?.['name'] || 'N/A'}</td>
                                <td>{getStatusBadge(agent['status'] || 'active', agent)}</td>
                                <td><small>{new Date(agent['createdAt']).toLocaleString()}</small></td>
                                <td>{agent['callStats']?.['todaysCalls'] || 0}</td>
                                <td>
                                  <Progress
                                    value={agent['callStats']?.['successRate'] || 0}
                                    style={{ height: '15px' }}
                                  >
                                    {agent['callStats']?.['successRate'] || 0}%
                                  </Progress>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabPane>

            {/* Agent List Tab */}
            <TabPane tabId="agent-list">
              <div className='content-dashboard'>
                <div className='title-area style-workshop'>
                  <h4>All Agents</h4>
                </div>
                <div className="table-container filters-table">
                  {/* Search and Filters */}
                  <div className='header-table-filters advanced-table-filters'>
                    <div className='left-area'>
                      <div className='filter-box'>
                        <div className='searchbox'>
                          <TextField
                            fullWidth
                            hiddenLabel
                            size="small"
                            name='search'
                            variant="standard"
                            placeholder="Search agents..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <i className='icon'>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                              <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                            </svg>
                          </i>
                        </div>
                      </div>
                      <div className='filter-box'>
                        <Input
                          type="select"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="">All Statuses</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="busy">Busy</option>
                          <option value="error">Error</option>
                        </Input>
                      </div>
                    </div>
                    <div className='right-deals-filtes'>
                      <UncontrolledDropdown>
                        <DropdownToggle
                          size="sm"
                          color="secondary"
                          caret
                          disabled={exportLoading}
                          className="mr-2"
                        >
                          <i className="fa fa-download mr-1"></i>
                          {exportLoading ? 'Exporting...' : 'Export All Agents'}
                        </DropdownToggle>
                        <DropdownMenu right>
                          <DropdownItem onClick={() => handleExportAllAgents('csv')}>
                            <i className="fa fa-file-text-o mr-2"></i>Export as CSV
                          </DropdownItem>
                          <DropdownItem onClick={() => handleExportAllAgents('excel')}>
                            <i className="fa fa-file-excel-o mr-2"></i>Export as Excel
                          </DropdownItem>
                          <DropdownItem onClick={() => handleExportAllAgents('pdf')}>
                            <i className="fa fa-file-pdf-o mr-2"></i>Export as PDF
                          </DropdownItem>
                          <DropdownItem divider />
                          <DropdownItem header>
                            <small className="text-muted">
                              Exports all {agentStats?.summary?.total || '0'} agents
                              {searchTerm && ` matching "${searchTerm}"`}
                              {statusFilter && ` (Status: ${statusFilter})`}
                            </small>
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </div>
                  </div>
                  <div className='table-area'>
                    <BulkActions
                      selectedItems={selectedAgents}
                      totalItems={adminAgents.length}
                      onSelectAll={handleSelectAll}
                      onDeselectAll={handleDeselectAll}
                      actions={bulkActions}
                      onAction={handleBulkAction}
                      isLoading={loading}
                    />

                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th width="30">
                            <Checkbox checked={selectAll} onChange={handleSelectAll} />
                          </th>
                          <th>Name</th>
                          <th>Workspace</th>
                          <th>Status</th>
                          <th>Calls</th>
                          <th>Success Rate</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>

                        {adminAgents.map(agent => (
                          <tr key={agent['_id']}>
                            <td>
                              <Checkbox checked={selectedAgents.includes(agent['_id'])} onChange={() => handleAgentSelect(agent['_id'])} />
                            </td>
                            <td>
                              <strong>{agent['name']}</strong>
                              <br />
                              <small className="text-muted">{agent['description']}</small>
                            </td>
                            <td>{agent['workspaceId']?.['name'] || 'N/A'}</td>
                            <td>{getStatusBadge(agent['status'] || 'active', agent)}</td>
                            <td>{agent['callStats']?.['totalCalls'] || 0}</td>
                            <td>
                              {agent['callStats']?.['totalCalls'] > 0 ? `${Math.round((agent['callStats']['successfulCalls'] / agent['callStats']['totalCalls']) * 100)}%` : 'N/A'}
                            </td>
                            <td>{new Date(agent['createdAt']).toLocaleDateString()}</td>
                            <td>
                              <div className='action-box'>
                                <button className='action-btn' color="info" onClick={() => openAgentDetails(agent)} title="View Details">
                                  <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#323B43" /><path d="M12 14.165C10.805 14.165 9.83496 13.195 9.83496 12C9.83496 10.805 10.805 9.83501 12 9.83501C13.195 9.83501 14.165 10.805 14.165 12C14.165 13.195 13.195 14.165 12 14.165ZM12 10.585C11.22 10.585 10.585 11.22 10.585 12C10.585 12.78 11.22 13.415 12 13.415C12.78 13.415 13.415 12.78 13.415 12C13.415 11.22 12.78 10.585 12 10.585Z" fill="#323B43" /><path d="M12 16.51C10.12 16.51 8.34504 15.41 7.12504 13.5C6.59504 12.675 6.59504 11.33 7.12504 10.5C8.35004 8.58999 10.125 7.48999 12 7.48999C13.875 7.48999 15.65 8.58999 16.87 10.5C17.4 11.325 17.4 12.67 16.87 13.5C15.65 15.41 13.875 16.51 12 16.51ZM12 8.23999C10.385 8.23999 8.84004 9.20999 7.76004 10.905C7.38504 11.49 7.38504 12.51 7.76004 13.095C8.84004 14.79 10.385 15.76 12 15.76C13.615 15.76 15.16 14.79 16.24 13.095C16.615 12.51 16.615 11.49 16.24 10.905C15.16 9.20999 13.615 8.23999 12 8.23999Z" fill="#323B43" /></svg></i>
                                </button>

                                {/* Start/Stop button for active agents, Activate button for inactive */}
                                {(agent['status'] || 'active') === 'active'
                                  ? (
                                    <>
                                      {agent['running']
                                        ? <button className='action-btn' color="warning" onClick={() => handleAgentAction(agent['_id'], 'stop')} title="Stop Agent">
                                          <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#FF8B00" /><path d="M10.32 16.875H8.505C7.575 16.875 7.125 16.445 7.125 15.555V8.445C7.125 7.555 7.575 7.125 8.505 7.125H10.32C11.25 7.125 11.7 7.555 11.7 8.445V15.555C11.7 16.445 11.25 16.875 10.32 16.875ZM8.505 7.875C7.965 7.875 7.875 8.01 7.875 8.445V15.555C7.875 15.99 7.96 16.125 8.505 16.125H10.32C10.86 16.125 10.95 15.99 10.95 15.555V8.445C10.95 8.01 10.865 7.875 10.32 7.875H8.505Z" fill="#FF8B00" /><path d="M15.4948 16.875H13.6798C12.7498 16.875 12.2998 16.445 12.2998 15.555V8.445C12.2998 7.555 12.7498 7.125 13.6798 7.125H15.4948C16.4248 7.125 16.8748 7.555 16.8748 8.445V15.555C16.8748 16.445 16.4248 16.875 15.4948 16.875ZM13.6798 7.875C13.1398 7.875 13.0498 8.01 13.0498 8.445V15.555C13.0498 15.99 13.1348 16.125 13.6798 16.125H15.4948C16.0348 16.125 16.1248 15.99 16.1248 15.555V8.445C16.1248 8.01 16.0398 7.875 15.4948 7.875H13.6798Z" fill="#FF8B00" /></svg></i>
                                        </button>
                                        : <button className='action-btn' color="success" onClick={() => handleAgentAction(agent['_id'], 'start')} title="Start Agent">
                                          <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#138B3F" /><path d="M10.1917 16.1708C9.82417 16.1708 9.48 16.0833 9.17667 15.9083C8.45917 15.4942 8.0625 14.66 8.0625 13.5633V10.4425C8.0625 9.34584 8.45917 8.51167 9.17667 8.09751C9.89417 7.68334 10.81 7.75917 11.7667 8.30751L14.4675 9.86501C15.4183 10.4133 15.9433 11.1717 15.9433 12C15.9433 12.8283 15.4183 13.5867 14.4675 14.135L11.7667 15.6925C11.2183 16.0133 10.6817 16.1708 10.1917 16.1708ZM10.1917 8.71001C9.97583 8.71001 9.7775 8.75667 9.60833 8.85584C9.17667 9.10667 8.9375 9.66667 8.9375 10.4425V13.5575C8.9375 14.3275 9.17667 14.8933 9.61417 15.1442C10.0517 15.395 10.6583 15.3192 11.3292 14.9342L14.03 13.3767C14.7008 12.9917 15.0683 12.5017 15.0683 12C15.0683 11.4983 14.7008 11.0083 14.03 10.6233L11.3292 9.06584C10.915 8.82667 10.53 8.71001 10.1917 8.71001Z" fill="#138B3F" /></svg></i>
                                        </button>
                                      }

                                      <button
                                        className='action-btn'
                                        style={{ backgroundColor: '#dc3545' }}
                                        onClick={() => handleAgentAction(agent['_id'], 'deactivate')}
                                        title="Deactivate Agent"
                                      >
                                        <i className="fa fa-power-off" style={{ color: '#fff' }}></i>
                                      </button>
                                    </>
                                  )
                                  : <button
                                    className='action-btn'
                                    style={{ backgroundColor: '#28a745' }}
                                    onClick={() => handleAgentAction(agent['_id'], 'activate')}
                                    title="Activate Agent"
                                  >
                                    <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#138B3F" /><path d="M10.1917 16.1708C9.82417 16.1708 9.48 16.0833 9.17667 15.9083C8.45917 15.4942 8.0625 14.66 8.0625 13.5633V10.4425C8.0625 9.34584 8.45917 8.51167 9.17667 8.09751C9.89417 7.68334 10.81 7.75917 11.7667 8.30751L14.4675 9.86501C15.4183 10.4133 15.9433 11.1717 15.9433 12C15.9433 12.8283 15.4183 13.5867 14.4675 14.135L11.7667 15.6925C11.2183 16.0133 10.6817 16.1708 10.1917 16.1708ZM10.1917 8.71001C9.97583 8.71001 9.7775 8.75667 9.60833 8.85584C9.17667 9.10667 8.9375 9.66667 8.9375 10.4425V13.5575C8.9375 14.3275 9.17667 14.8933 9.61417 15.1442C10.0517 15.395 10.6583 15.3192 11.3292 14.9342L14.03 13.3767C14.7008 12.9917 15.0683 12.5017 15.0683 12C15.0683 11.4983 14.7008 11.0083 14.03 10.6233L11.3292 9.06584C10.915 8.82667 10.53 8.71001 10.1917 8.71001Z" fill="#138B3F" /></svg></i>
                                  </button>
                                }

                                <button className='action-btn' color="warning" onClick={() => handleAgentAction(agent['_id'], 'restart')} title="Restart Agent">
                                  <i className="fa fa-undo"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {/* Pagination */}
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination>
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink
                            previous
                            onClick={() => setCurrentPage(currentPage - 1)}
                          />
                        </PaginationItem>

                        {[...Array(Math.min(5, agentsPagination?.['pages'] || 1))].map((_, i) => {
                          const page = i + 1;
                          return (
                            <PaginationItem key={page} active={page === currentPage}>
                              <PaginationLink onClick={() => setCurrentPage(page)}>
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        })}

                        <PaginationItem disabled={currentPage === (agentsPagination?.['pages'] || 1)}>
                          <PaginationLink
                            next
                            onClick={() => setCurrentPage(currentPage + 1)}
                          />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  </div>
                </div>
              </div>
            </TabPane>

            {/* Performance Tab */}
            <TabPane tabId="performance">
              {renderPerformanceTab()}
            </TabPane>

            {/* Costs Tab */}
            <TabPane tabId="cost-analytics">
              <div className='counter-sections mt-3'>
                <div className='counter-box warning-box'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Total Monthly Cost</h5>
                      <div className='CountUp-box'>
                        <h3>${costAnalytics?.['totalCost']?.toLocaleString() || 0}</h3>
                      </div>
                    </div>
                    <div className='right-icon'>
                      <span className='icon'>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle opacity="0.1" cx="16" cy="16" r="16" fill="#DF9524" />
                          <path d="M16.9334 19.6134H15.2601C14.1668 19.6134 13.2801 18.6934 13.2801 17.5601C13.2801 17.2867 13.5068 17.0601 13.7801 17.0601C14.0534 17.0601 14.2801 17.2867 14.2801 17.5601C14.2801 18.1401 14.7201 18.6134 15.2601 18.6134H16.9334C17.3668 18.6134 17.7268 18.2267 17.7268 17.7601C17.7268 17.1801 17.5201 17.0667 17.1801 16.9467L14.4934 16.0001C13.9734 15.8201 13.2734 15.4334 13.2734 14.2401C13.2734 13.2134 14.0801 12.3867 15.0668 12.3867H16.7401C17.8334 12.3867 18.7201 13.3067 18.7201 14.4401C18.7201 14.7134 18.4934 14.9401 18.2201 14.9401C17.9468 14.9401 17.7201 14.7134 17.7201 14.4401C17.7201 13.8601 17.2801 13.3867 16.7401 13.3867H15.0668C14.6334 13.3867 14.2734 13.7734 14.2734 14.2401C14.2734 14.8201 14.4801 14.9334 14.8201 15.0534L17.5068 16.0001C18.0268 16.1801 18.7268 16.5667 18.7268 17.7601C18.7201 18.7801 17.9201 19.6134 16.9334 19.6134Z" fill="#DF9524" />
                          <path d="M16 20.5C15.7267 20.5 15.5 20.2733 15.5 20V12C15.5 11.7267 15.7267 11.5 16 11.5C16.2733 11.5 16.5 11.7267 16.5 12V20C16.5 20.2733 16.2733 20.5 16 20.5Z" fill="#DF9524" />
                          <path d="M16.0002 23.1667C12.0468 23.1667 8.8335 19.9534 8.8335 16C8.8335 12.0467 12.0468 8.83337 16.0002 8.83337C19.9535 8.83337 23.1668 12.0467 23.1668 16C23.1668 19.9534 19.9535 23.1667 16.0002 23.1667ZM16.0002 9.83337C12.6002 9.83337 9.8335 12.6 9.8335 16C9.8335 19.4 12.6002 22.1667 16.0002 22.1667C19.4002 22.1667 22.1668 19.4 22.1668 16C22.1668 12.6 19.4002 9.83337 16.0002 9.83337Z" fill="#DF9524" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
                <div className='counter-box primery-box'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Total AI Calls</h5>
                      <div className='CountUp-box'>
                        <h3>{(costAnalytics?.['breakdown']?.['openai']?.['calls'] + costAnalytics?.['breakdown']?.['anthropic']?.['calls']) || 0}</h3>
                      </div>
                    </div>
                    <div className='right-icon'>
                      <span className='icon'>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle opacity="0.1" cx="16" cy="16" r="16" fill="#7E1FD2" />
                          <path d="M19.6335 23.1667C18.8802 23.1667 18.0868 22.9867 17.2668 22.64C16.4668 22.3 15.6602 21.8334 14.8735 21.2667C14.0935 20.6934 13.3402 20.0534 12.6268 19.3534C11.9202 18.64 11.2802 17.8867 10.7135 17.1134C10.1402 16.3134 9.68016 15.5134 9.3535 14.74C9.00683 13.9134 8.8335 13.1134 8.8335 12.36C8.8335 11.84 8.92683 11.3467 9.10683 10.8867C9.2935 10.4134 9.5935 9.97337 10.0002 9.59337C10.5135 9.08671 11.1002 8.83337 11.7268 8.83337C11.9868 8.83337 12.2535 8.89337 12.4802 9.00004C12.7402 9.12004 12.9602 9.30004 13.1202 9.54004L14.6668 11.72C14.8068 11.9134 14.9135 12.1 14.9868 12.2867C15.0735 12.4867 15.1202 12.6867 15.1202 12.88C15.1202 13.1334 15.0468 13.38 14.9068 13.6134C14.8068 13.7934 14.6535 13.9867 14.4602 14.18L14.0068 14.6534C14.0135 14.6734 14.0202 14.6867 14.0268 14.7C14.1068 14.84 14.2668 15.08 14.5735 15.44C14.9002 15.8134 15.2068 16.1534 15.5135 16.4667C15.9068 16.8534 16.2335 17.16 16.5402 17.4134C16.9202 17.7334 17.1668 17.8934 17.3135 17.9667L17.3002 18L17.7868 17.52C17.9935 17.3134 18.1935 17.16 18.3868 17.06C18.7535 16.8334 19.2202 16.7934 19.6868 16.9867C19.8602 17.06 20.0468 17.16 20.2468 17.3L22.4602 18.8734C22.7068 19.04 22.8868 19.2534 22.9935 19.5067C23.0935 19.76 23.1402 19.9934 23.1402 20.2267C23.1402 20.5467 23.0668 20.8667 22.9268 21.1667C22.7868 21.4667 22.6135 21.7267 22.3935 21.9667C22.0135 22.3867 21.6002 22.6867 21.1202 22.88C20.6602 23.0667 20.1602 23.1667 19.6335 23.1667ZM11.7268 9.83337C11.3602 9.83337 11.0202 9.99337 10.6935 10.3134C10.3868 10.6 10.1735 10.9134 10.0402 11.2534C9.90016 11.6 9.8335 11.9667 9.8335 12.36C9.8335 12.98 9.98016 13.6534 10.2735 14.3467C10.5735 15.0534 10.9935 15.7867 11.5268 16.52C12.0602 17.2534 12.6668 17.9667 13.3335 18.64C14.0002 19.3 14.7202 19.9134 15.4602 20.4534C16.1802 20.98 16.9202 21.4067 17.6535 21.7134C18.7935 22.2 19.8602 22.3134 20.7402 21.9467C21.0802 21.8067 21.3802 21.5934 21.6535 21.2867C21.8068 21.12 21.9268 20.94 22.0268 20.7267C22.1068 20.56 22.1468 20.3867 22.1468 20.2134C22.1468 20.1067 22.1268 20 22.0735 19.88C22.0535 19.84 22.0135 19.7667 21.8868 19.68L19.6735 18.1067C19.5402 18.0134 19.4202 17.9467 19.3068 17.9C19.1602 17.84 19.1002 17.78 18.8735 17.92C18.7402 17.9867 18.6202 18.0867 18.4868 18.22L17.9802 18.72C17.7202 18.9734 17.3202 19.0334 17.0135 18.92L16.8335 18.84C16.5602 18.6934 16.2402 18.4667 15.8868 18.1667C15.5668 17.8934 15.2202 17.5734 14.8002 17.16C14.4735 16.8267 14.1468 16.4734 13.8068 16.08C13.4935 15.7134 13.2668 15.4 13.1268 15.14L13.0468 14.94C13.0068 14.7867 12.9935 14.7 12.9935 14.6067C12.9935 14.3667 13.0802 14.1534 13.2468 13.9867L13.7468 13.4667C13.8802 13.3334 13.9802 13.2067 14.0468 13.0934C14.1002 13.0067 14.1202 12.9334 14.1202 12.8667C14.1202 12.8134 14.1002 12.7334 14.0668 12.6534C14.0202 12.5467 13.9468 12.4267 13.8535 12.3L12.3068 10.1134C12.2402 10.02 12.1602 9.95337 12.0602 9.90671C11.9535 9.86004 11.8402 9.83337 11.7268 9.83337ZM17.3002 18.0067L17.1935 18.46L17.3735 17.9934C17.3402 17.9867 17.3135 17.9934 17.3002 18.0067Z" fill="#7E1FD2" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
                <div className='counter-box info-box'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Voice Minutes</h5>
                      <div className='CountUp-box'>
                        <h3 className="text-info">{costAnalytics?.['breakdown']?.['voice']?.['minutes'] || 0}</h3>
                      </div>
                    </div>
                    <div className='right-icon'>
                      <span className='icon'>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle opacity="0.1" cx="16" cy="16" r="16" fill="#4D6BFE" />
                          <path d="M12 17.9267C11.7267 17.9267 11.5 17.7 11.5 17.4267V14.5667C11.5 14.2933 11.7267 14.0667 12 14.0667C12.2733 14.0667 12.5 14.2933 12.5 14.5667V17.4267C12.5 17.7067 12.2733 17.9267 12 17.9267Z" fill="#4D6BFE" />
                          <path d="M14 18.88C13.7267 18.88 13.5 18.6533 13.5 18.38V13.62C13.5 13.3467 13.7267 13.12 14 13.12C14.2733 13.12 14.5 13.3467 14.5 13.62V18.38C14.5 18.66 14.2733 18.88 14 18.88Z" fill="#4D6BFE" />
                          <path d="M16 19.8333C15.7267 19.8333 15.5 19.6066 15.5 19.3333V12.6666C15.5 12.3933 15.7267 12.1666 16 12.1666C16.2733 12.1666 16.5 12.3933 16.5 12.6666V19.3333C16.5 19.6066 16.2733 19.8333 16 19.8333Z" fill="#4D6BFE" />
                          <path d="M18 18.88C17.7267 18.88 17.5 18.6533 17.5 18.38V13.62C17.5 13.3467 17.7267 13.12 18 13.12C18.2733 13.12 18.5 13.3467 18.5 13.62V18.38C18.5 18.66 18.2733 18.88 18 18.88Z" fill="#4D6BFE" />
                          <path d="M20 17.9267C19.7267 17.9267 19.5 17.7 19.5 17.4267V14.5667C19.5 14.2933 19.7267 14.0667 20 14.0667C20.2733 14.0667 20.5 14.2933 20.5 14.5667V17.4267C20.5 17.7067 20.2733 17.9267 20 17.9267Z" fill="#4D6BFE" />
                          <path d="M18.0002 23.1667H14.0002C10.3802 23.1667 8.8335 21.62 8.8335 18V14C8.8335 10.38 10.3802 8.83337 14.0002 8.83337H18.0002C21.6202 8.83337 23.1668 10.38 23.1668 14V18C23.1668 21.62 21.6202 23.1667 18.0002 23.1667ZM14.0002 9.83337C10.9268 9.83337 9.8335 10.9267 9.8335 14V18C9.8335 21.0734 10.9268 22.1667 14.0002 22.1667H18.0002C21.0735 22.1667 22.1668 21.0734 22.1668 18V14C22.1668 10.9267 21.0735 9.83337 18.0002 9.83337H14.0002Z" fill="#4D6BFE" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
                <div className='counter-box success-box'>
                  <div className='counter-start' >
                    <div className='left-text-area'>
                      <h5>Daily Average</h5>
                      <div className='CountUp-box'>
                        <h3>${((costAnalytics?.['totalCost'] || 0) / 30).toFixed(2)}</h3>
                      </div>
                    </div>
                    <div className='right-icon'>
                      <span className='icon'>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle opacity="0.1" cx="16" cy="16" r="16" fill="#16A34A70" />
                          <path d="M19.6188 22.7125H12.3812C10.6688 22.7125 9.28125 21.3188 9.28125 19.6063V14.975C9.28125 14.125 9.80625 13.0563 10.4813 12.5313L13.85 9.90626C14.8625 9.11876 16.4812 9.08126 17.5312 9.81876L21.3937 12.525C22.1375 13.0438 22.7188 14.1563 22.7188 15.0625V19.6125C22.7188 21.3188 21.3313 22.7125 19.6188 22.7125ZM14.425 10.6438L11.0563 13.2688C10.6125 13.6188 10.2188 14.4125 10.2188 14.975V19.6063C10.2188 20.8 11.1875 21.775 12.3812 21.775H19.6188C20.8125 21.775 21.7812 20.8063 21.7812 19.6125V15.0625C21.7812 14.4625 21.35 13.6313 20.8563 13.2938L16.9938 10.5875C16.2812 10.0875 15.1062 10.1125 14.425 10.6438Z" fill="#007EE5" />
                          <path d="M13.1874 19.2812C13.0687 19.2812 12.9499 19.2374 12.8562 19.1437C12.6749 18.9624 12.6749 18.6624 12.8562 18.4812L14.8562 16.4812C14.9562 16.3812 15.0874 16.3312 15.2312 16.3437C15.3687 16.3562 15.4937 16.4312 15.5749 16.5499L16.2562 17.5749L18.4749 15.3562C18.6562 15.1749 18.9562 15.1749 19.1374 15.3562C19.3187 15.5374 19.3187 15.8374 19.1374 16.0187L16.5124 18.6437C16.4124 18.7437 16.2812 18.7937 16.1374 18.7812C15.9999 18.7687 15.8749 18.6937 15.7937 18.5749L15.1124 17.5499L13.5187 19.1437C13.4249 19.2374 13.3062 19.2812 13.1874 19.2812Z" fill="#007EE5" />
                          <path d="M18.8125 17.4062C18.5562 17.4062 18.3438 17.1938 18.3438 16.9375V16.1562H17.5625C17.3063 16.1562 17.0938 15.9438 17.0938 15.6875C17.0938 15.4313 17.3063 15.2188 17.5625 15.2188H18.8125C19.0688 15.2188 19.2812 15.4313 19.2812 15.6875V16.9375C19.2812 17.1938 19.0688 17.4062 18.8125 17.4062Z" fill="#007EE5" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className='chart-box'>
                <div className='row'>
                  <div className='col-lg-6 col-md-12 col-sm-12'>
                    <div className='chart-area'>
                      <div className='table-area table-workspaces'>
                        <h4>Cost Breakdown by Provider</h4>
                        <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Doughnut
                            data={costBreakdownData}
                            options={{ 'responsive': true, 'maintainAspectRatio': false, 'plugins': { 'legend': { 'position': 'bottom' } } }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6 col-md-12 col-sm-12'>
                    <div className='chart-area'>
                      <div className='table-area table-workspaces'>
                        <h4>Provider Details</h4>
                        <Table>
                          <thead>
                            <tr>
                              <th>Provider</th>
                              <th>Cost</th>
                              <th>Usage</th>
                              <th>Avg Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(costAnalytics?.['breakdown'] || {}).map(([provider, data]) => (
                              <tr key={provider}>
                                <td className="text-capitalize">{provider}</td>
                                <td>${data['cost']?.toLocaleString() || 0}</td>
                                <td>
                                  {provider === 'voice' ? `${data['minutes']} min` : `${data['calls']} calls`}
                                </td>
                                <td>
                                  ${provider === 'voice' ? data['avgCostPerMinute']?.toFixed(3) || 0 : data['avgCostPerCall']?.toFixed(3) || 0}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </TabPane>
          </TabContent>
        </div>

        {/* Agent Details Sidebar */}
        <AgentSidebar
          isOpen={showSidebar}
          onClose={closeSidebar}
          agent={selectedAgentForDetails}
        />

        {/* Keeping modal temporarily commented for reference */}
        {/* <Modal isOpen={false} toggle={() => {}} size="xl" className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal`}>
          <ModalHeader toggle={() => {}}>
            Agent Details
          </ModalHeader>
          <ModalBody>
            <Form className="dashboard-form ">
              {false && (
                <div>
                  <Row className="mb-4">
                    <Col md={6}>
                      <Card>
                        <CardHeader>
                          <h6 className="mb-0">Agent Information</h6>
                        </CardHeader>
                        <CardBody>
                          <p><strong>Name:</strong> {selectedAgent['agent']['name']}</p>
                          <p><strong>Status:</strong> {getStatusBadge(selectedAgent['agent']['status'], selectedAgent['agent'])}</p>
                          <p><strong>Workspace:</strong> {selectedAgent['agent']['workspaceId']?.['name']}</p>
                          <p><strong>Created:</strong> {new Date(selectedAgent['agent']['createdAt']).toLocaleString()}</p>
                          <p><strong>Description:</strong> {selectedAgent['agent']['description']}</p>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card>
                        <CardHeader>
                          <h6 className="mb-0">Performance Stats</h6>
                        </CardHeader>
                        <CardBody>
                          <p><strong>Total Calls:</strong> {selectedAgent['performance']['totalCalls']}</p>
                          <p><strong>Success Rate:</strong> {Math.round(selectedAgent['performance']['successRate'] * 100)}%</p>
                          <p><strong>Average Duration:</strong> {Math.floor(selectedAgent['performance']['averageDuration'] / 60)}m</p>
                          <p><strong>Today's Calls:</strong> {selectedAgent['performance']['todaysCalls']}</p>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  <Row className="mb-4">
                    <Col md={6}>
                      <Card>
                        <CardHeader>
                          <h6 className="mb-0">Recent Calls</h6>
                        </CardHeader>
                        <CardBody style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <Table size="sm">
                            <thead>
                              <tr>
                                <th>Time</th>
                                <th>Duration</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {agentCalls.slice(0, 10).map(call => (
                                <tr key={call['_id']}>
                                  <td>{new Date(call['createdAt']).toLocaleString()}</td>
                                  <td>{Math.floor(call['duration'] / 60)}m</td>
                                  <td>
                                    <Badge color={call['status'] === 'completed' ? 'success' : 'warning'}>{call['status']}</Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </CardBody>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Card>
                        <CardHeader>
                          <h6 className="mb-0">Recent Logs</h6>
                        </CardHeader>
                        <CardBody style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {agentLogs.slice(0, 10).map((log, index) => (
                            <div key={index} className="mb-2">
                              <div className="d-flex justify-content-between">
                                <Badge color={log['level'] === 'error' ? 'danger' : log['level'] === 'warning' ? 'warning' : 'info'}>{log['level']}</Badge>
                                <small className="text-muted">{new Date(log['timestamp']).toLocaleTimeString()}</small>
                              </div>
                              <div className="mt-1">
                                <small>{log['message']}</small>
                              </div>
                            </div>
                          ))}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )}
            </Form>
          </ModalBody>
          <ModalFooter>
            <button className='btn-style-new' onClick={() => setShowModal(false)}>
              Close
            </button>
          </ModalFooter>
        </Modal> */}
      </div>
    </>

  );
};

export default AgentsManagement;